import { ReferenceImageStylePicker, PHOTO_STYLE_ID } from './ReferenceImageStylePicker';
import { ProductImageReferences } from './ProductImageReferences';
import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { Accordion } from '@/components/ui/Accordion';
import { useQuery } from '@tanstack/react-query';
import {
  Brain, Plus, ChevronDown, ChevronRight, Pencil, RefreshCw,
  Loader2, CheckCircle, Trash2, Save, X, Calendar,
  Copy, Download, Lock, Settings, Check, FilePlus2, ExternalLink, Info,
  Image as ImageIcon, HardDrive, Upload, Search, Wand2, Palette,
  GitBranch, Clapperboard, Film, Rocket, Square,
  Play, Replace,
} from 'lucide-react';
import { labApi, storeApi, studioApi, type Store } from '../../lib/api';
import { onSocketReady, unsubscribeFromLaboratorium, getSocket } from '@/lib/socket';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/EmptyState';
import { useConfirmDialog } from '@/contexts/ConfirmDialogContext';
import type { LabProduct, LabContent, KnowledgeBase, LabReferenceImage, LabReferenceImageKind, LabProductImage, ProductImageStyleCard } from './types';
import { VideoAssetsPanel } from './VideoAssetsPanel';
import { VideoBankPanel } from './VideoBankPanel';
import { AdCreativesPanel } from './AdCreativesPanel';
import { SocialPlannerPanel } from './SocialPlannerPanel';
import { AssetTextReplacementModal } from './AssetTextReplacementModal';
import { IterateVariantModal } from './IterateVariantModal';
import { CampaignModal } from './CampaignModal';
import { CONTENT_SECTIONS, getContentStatusBadge } from './constants';
import { LabConfiguratorShell } from './LabConfiguratorShell';
import { LabContentImportModal } from './LabContentImportModal';
import { isLabPipelineCancelled, getKnowledgePipelineFailure, getStaleContentPipelineFailure } from './knowledgeGenerationState';
import { hasProductVisualReference } from './productImageReadiness';
import { getProductImagesRunFailure } from './generationRunState';

function ContentResourceSection({ enabled, id, title, icon, defaultOpen, children }: {
  enabled: boolean; id: string; title: string; icon: ReactNode; defaultOpen?: boolean; children: ReactNode;
}) {
  if (!enabled) return <>{children}</>;
  return <Accordion items={[{ id, title, icon, defaultOpen, content: <div className="space-y-3">{children}</div> }]} />;
}

const canGeneratePageImages = (content: LabContent) => content.status === 'COMPLETE' || content.sections?.__product_page_generation?.original === 'gallery';

/* ─── Field titles for KB sections ─── */
const FIELD_TITLES: Record<string, string> = {
  product_summary: 'Podsumowanie produktu',
  competitor_analysis: 'Analiza konkurencji',
  marketing_angle: 'Marketing Angle',
  features_benefits_values: 'Funkcje, Korzyści, Wartości',
  customer_reviews_analysis: 'Analiza opinii klientów',
  problem_mechanism: 'Mechanizm problemu',
  solution_mechanism: 'Mechanizm rozwiązania',
  objections_belief_map: 'Obiekcje i mapa przekonań',
  unique_mechanism_usp: 'Unikalny Mechanizm / USP',
  ideal_customer_profile: 'Idealny profil klienta',
  before_after_diagram: 'Before & After Diagram',
  product_name: 'Nazwa produktu',
  summary: 'Podsumowanie produktu',
  ingredients_spec: 'Składniki / Specyfikacja',
  usp: 'USP - Unikalny Mechanizm',
  competitor_comparison: 'Porównanie z konkurencją',
  pricing_strategy: 'Strategia cenowa',
  buyer_persona: 'Buyer Persona',
  pain_desire: 'Ból / Pragnienie',
  objections: 'Obiekcje klientów',
  emotional_triggers: 'Emocjonalne triggery',
  audience_language: 'Język grupy docelowej',
  niche_analysis: 'Analiza niszy',
  seasonality_trends: 'Sezonowość i trendy',
  reviews_analysis: 'Analiza opinii',
  sales_arguments: 'Argumenty sprzedażowe',
  social_proof_strategy: 'Social Proof Strategy',
  guarantees_risk_reversal: 'Gwarancje i Risk Reversal',
  urgency_scarcity: 'Urgency & Scarcity',
  product_names: 'Nazwa produktu',
  features_benefits: 'Funkcje, Korzyści, Wartości',
  before_after: 'Before & After State',
  source_bias_competitor_map: 'Źródła, bias i konkurenci',
  competitor_marketing_angle: 'Marketing angle konkurencji',
  review_mining_voc: 'Review mining i VOC',
  persona_main_avatar: 'MAIN AVATAR',
  mass_desire_buyer_journey: 'Mass desire i buyer journey',
  before_after_state: 'Before / After State',
  awareness_entry_level: 'Awareness i poziom wejścia',
  positioning_market_gap: 'Pozycjonowanie i luka rynku',
  naming_and_angle: 'Naming i angle',
  finance_unit_economics: 'Unit economics',
  offer_architecture_ecom: 'Architektura oferty',
  guarantee_risk_reversal: 'Gwarancja',
  coherence_audit: 'Audyt spójności',
};

const KB_SECTION_TITLE_OVERRIDES: Record<string, string> = {
  problem_mechanism_options: 'Mechanizm problemu',
  solution_mechanism_options: 'Mechanizm rozwiązania',
  product_unique_mechanism_options: 'Unikalny mechanizm działania',
  price_package_strategy: 'Pakiety cenowe Popcorn Strategy',
  guarantee_risk_reversal: 'Gwarancja',
  offer_architecture_options: 'Architektura oferty',
  brand_name_options: 'Nazwa produktu',
  unique_mechanism_usp: 'Unikalny mechanizm działania',
};

const HIDDEN_KB_SECTION_FIELDS = new Set([
  'price_package_strategy',
  'naming_strategy_options',
  'naming_strategy_choice',
]);

/* ─── Content type label map for default titles ─── */
const CONTENT_TYPE_LABEL: Record<string, string> = {
  ADVERTORIAL: 'Advertorial',
  LISTICALE: 'Listical',
  QUIZ: 'Quiz',
  LANDING: 'Produktowe',
  AD_COPY: 'Treści reklamowe',
  VIDEO_SCRIPT: 'Reklama wideo',
  EXPLAINER: 'Explainer',
  VSL: 'VSL',
  VSL_STORY: 'VSL Story',
  HOOKS: 'Hooki',
  AD_GRAPHIC: 'Reklama statyczna',
  AD_CREATIVE: 'Kreacja reklamowa',
  UGC_SCRIPT: 'Reklama UGC',
  SOCIAL_POST: 'Social Media',
};

const REQUIRED_REFERENCE_IMAGE_ORDER: LabReferenceImageKind[] = ['PRODUCT', 'PACKAGING', 'COMBO', 'BEFORE_AFTER'];
const OPTIONAL_REFERENCE_IMAGE_ORDER: LabReferenceImageKind[] = ['PRODUCT_STYLE'];
const BUNDLE_REFERENCE_IMAGE_ORDER: LabReferenceImageKind[] = ['BUNDLE_2', 'BUNDLE_3', 'BUNDLE_CUSTOM'];
const BUNDLE_REFERENCE_IMAGE_KINDS = new Set<LabReferenceImageKind>(BUNDLE_REFERENCE_IMAGE_ORDER);
const REFERENCE_IMAGE_ORDER: LabReferenceImageKind[] = [
  'PRODUCT',
  'PACKAGING',
  'COMBO',
  ...BUNDLE_REFERENCE_IMAGE_ORDER,
  'BEFORE_AFTER',
  ...OPTIONAL_REFERENCE_IMAGE_ORDER,
];
const NEW_REFERENCE_TYPOGRAPHY_ID = 'new-reference-typography';
const PRODUCT_STYLE_REFERENCE_STYLE_ID = 'reference-product-photo';
const REFERENCE_FIELD_KIND: Record<string, LabReferenceImageKind> = {
  product_reference: 'PRODUCT',
  packaging_reference: 'PACKAGING',
  combo_reference: 'COMBO',
  before_after_reference: 'BEFORE_AFTER',
};

const PRODUCT_IMAGE_LANGUAGE_OPTIONS = [
  { value: 'pl', label: 'Polski' },
  { value: 'en', label: 'Angielski' },
  { value: 'de', label: 'Niemiecki' },
  { value: 'cs', label: 'Czeski' },
  { value: 'sk', label: 'Słowacki' },
  { value: 'ro', label: 'Rumuński' },
  { value: 'hu', label: 'Węgierski' },
  { value: 'es', label: 'Hiszpański' },
  { value: 'fr', label: 'Francuski' },
  { value: 'it', label: 'Włoski' },
];

const REFERENCE_IMAGE_LABELS: Record<LabReferenceImageKind, string> = {
  PRODUCT: 'Produkt',
  PACKAGING: 'Opakowanie',
  COMBO: 'Combo',
  BEFORE_AFTER: 'Typografia',
  PRODUCT_STYLE: 'Referencyjne zdjęcie produktowe',
  BUNDLE_1: 'Pakiet 1x',
  BUNDLE_2: 'Pakiet 2x',
  BUNDLE_3: 'Pakiet 3x',
  BUNDLE_CUSTOM: 'Pakiet niestandardowy',
};

const REFERENCE_IMAGE_DESCRIPTIONS: Record<LabReferenceImageKind, string> = {
  PRODUCT: 'Czysty packshot produktu jako bazowa referencja wyglądu i proporcji.',
  PACKAGING: 'Czysty packshot opakowania jako bazowa referencja wyglądu i proporcji.',
  COMBO: 'Produkt z opakowaniem jako bazowa referencja zestawu i proporcji.',
  BEFORE_AFTER: 'Neutralna plansza tekstowa jako bazowa referencja fontów i proporcji.',
  PRODUCT_STYLE: 'Opcjonalne gotowe zdjęcie produktowe jako referencja stylu, światła, kadru i obróbki kolejnych grafik.',
  BUNDLE_1: 'Packshot jednej sztuki gotowy do pierwszej karty cenowej.',
  BUNDLE_2: 'Packshot dokładnie dwóch sztuk gotowy do drugiej karty cenowej.',
  BUNDLE_3: 'Packshot dokładnie trzech sztuk gotowy do trzeciej karty cenowej.',
  BUNDLE_CUSTOM: 'Packshot wskazanej liczby sztuk do własnego wariantu pakietu.',
};

function referenceImageDisplayTitle(image: Pick<LabReferenceImage, 'kind' | 'title'>): string {
  return image.kind === 'BEFORE_AFTER'
    ? REFERENCE_IMAGE_LABELS.BEFORE_AFTER
    : image.title || REFERENCE_IMAGE_LABELS[image.kind];
}

const TYPOGRAPHY_STYLE_OPTIONS = [
  {
    id: 'premium-clean',
    title: 'Premium Clean',
    description: 'Nowoczesny bold sans, bardzo duży nagłówek i maksymalna czytelność mobile.',
    previewClass: 'bg-white text-slate-950',
    eyebrowClass: 'text-sm font-semibold uppercase tracking-wide text-slate-500',
    headingClass: 'text-3xl xl:text-4xl font-black leading-none text-slate-950',
    headingBoxClass: '',
    subheadingClass: 'text-lg xl:text-xl font-extrabold text-slate-700',
    bodyClass: 'text-sm xl:text-base font-medium leading-snug text-slate-600',
    promptInstruction: [
      'Styl Premium Clean.',
      'Użyj wyłącznie neutralnych tekstów: nagłówek "Lorem ipsum", podnagłówek "Dolor sit amet", akapit "Consectetur adipiscing elit. Tekst krótki, duży i czytelny na mobile."',
      'Białe tło, nowoczesny bardzo gruby sans-serif, ogromny czarny nagłówek bez boxa, mocny kontrast i maksymalna czytelność mobile.',
    ].join(' '),
  },
  {
    id: 'editorial-beauty',
    title: 'Editorial Beauty',
    description: 'Editorial beauty z mocnym ciemnym boxem i spokojnym rytmem tekstu.',
    previewClass: 'bg-[#faf7f2] text-[#2a201b]',
    eyebrowClass: 'text-sm font-medium uppercase tracking-wide text-[#8a7569]',
    headingClass: 'font-serif text-3xl xl:text-4xl font-bold leading-none text-white',
    headingBoxClass: 'inline-flex rounded-lg bg-[#1e1e1e] px-4 py-3 self-start',
    subheadingClass: 'text-lg xl:text-xl font-semibold text-[#5b473d]',
    bodyClass: 'text-sm xl:text-base leading-snug text-[#7a685f]',
    promptInstruction: [
      'Styl Editorial Beauty.',
      'Użyj wyłącznie neutralnych tekstów: nagłówek "Lorem ipsum", podnagłówek "Dolor sit amet", akapit "Consectetur adipiscing elit. Tekst krótki, duży i czytelny na mobile."',
      'Najważniejszy wymóg: główny nagłówek musi być umieszczony na ciemnym prostokątnym boxie w kolorze #1e1e1e, z białym tekstem.',
      'Nagłówek może być elegancki editorial serif, a podnagłówek i akapit spokojne, czytelne, bezszeryfowe. Nie generuj stylu Premium Clean ani Ornamental Luxe.',
    ].join(' '),
  },
  {
    id: 'ornamental-luxe',
    title: 'Ornamental Luxe',
    description: 'Ozdobny nagłówek w klimacie Allenoire i surowy sans dla podnagłówka.',
    previewClass: 'bg-[#fbfaf7] text-[#1f1712]',
    eyebrowClass: 'text-sm font-medium uppercase tracking-wide text-[#9a8064]',
    headingClass: 'font-serif text-3xl xl:text-4xl italic font-semibold leading-none',
    headingBoxClass: '',
    subheadingClass: 'text-lg xl:text-xl font-extrabold text-[#2f2a26]',
    bodyClass: 'text-sm xl:text-base font-medium leading-snug text-[#6f6259]',
    promptInstruction: [
      'Styl Ornamental Luxe.',
      'Użyj wyłącznie neutralnych tekstów: nagłówek "Lorem ipsum", podnagłówek "Dolor sit amet", akapit "Consectetur adipiscing elit. Tekst krótki, duży i czytelny na mobile."',
      'Nagłówek ma być ozdobny, wysokokontrastowy, w klimacie Allenoire, bez ciemnego boxa. Podnagłówek i akapit mają być surowe, czytelne, sans-serif.',
    ].join(' '),
  },
];

function selectedTypographyStyleInstruction(styleId: string): string | undefined {
  const option = TYPOGRAPHY_STYLE_OPTIONS.find(item => item.id === styleId);
  if (!option) return undefined;
  return `Wybrana karta stylu typografii: ${option.title}. ${option.promptInstruction}`;
}

function selectedTypographyStyleTitle(styleId: string): string {
  return TYPOGRAPHY_STYLE_OPTIONS.find(item => item.id === styleId)?.title || 'Premium Clean';
}

type TypographyStyleOption = (typeof TYPOGRAPHY_STYLE_OPTIONS)[number];

function TypographyStylePreviewCard({
  option,
  selected,
  disabled = false,
  onClick,
}: {
  option: TypographyStyleOption;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`overflow-hidden rounded-xl border text-left transition-colors disabled:opacity-60 ${
        selected
          ? 'border-accent-primary bg-accent-primary/10 shadow-[0_0_0_1px_rgba(74,158,255,0.35)]'
          : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'
      }`}
    >
      <div className={`aspect-[4/5] min-h-0 overflow-hidden p-5 xl:p-7 flex flex-col justify-between ${option.previewClass}`}>
        <div className={option.eyebrowClass}>Nagłówek</div>
        <div className="space-y-5">
          {option.headingBoxClass ? (
            <div className={option.headingBoxClass}>
              <div className={option.headingClass}>
                <span className="block">Lorem</span>
                <span className="block">ipsum</span>
              </div>
            </div>
          ) : (
            <div className={option.headingClass}>
              <span className="block">Lorem</span>
              <span className="block">ipsum</span>
            </div>
          )}
          <div className={option.subheadingClass}>Dolor sit amet</div>
          <div className={option.bodyClass}>Consectetur adipiscing elit. Tekst krótki, duży i czytelny na mobile.</div>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2">
          <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-accent-primary' : 'border-border-strong'}`}>
            {selected && <span className="w-2 h-2 rounded-full bg-accent-primary" />}
          </span>
          <span className="text-sm font-semibold text-text-primary">{option.title}</span>
        </div>
        <p className="text-xs text-text-muted mt-1.5 leading-relaxed">{option.description}</p>
      </div>
    </button>
  );
}

type ProductImageStyleCardOption = ProductImageStyleCard & {
  isLocked?: boolean;
  lockReason?: string;
};

function productStyleReferenceCard(args: {
  hasReference: boolean;
  imageUrl?: string | null;
}): ProductImageStyleCardOption {
  return {
    id: PRODUCT_STYLE_REFERENCE_STYLE_ID,
    name: 'Referencyjne zdjęcie produktowe',
    description: args.hasReference
      ? 'Użyj dodanego gotowego zdjęcia jako wzorca światła, tła, kadru, moodu i obróbki kolejnych grafik.'
      : 'Dodaj gotowe zdjęcie produktowe w Zdjęciach referencyjnych, żeby odblokować ten styl.',
    imageUrl: args.hasReference ? args.imageUrl || null : null,
    prompt: '',
    accentColor: '#f97316',
    sortOrder: 999,
    isActive: true,
    isLocked: !args.hasReference,
    lockReason: 'Dodaj gotowe zdjęcie produktowe w Zdjęciach referencyjnych.',
  };
}

interface SectionItemData {
  original: string;
  edited?: string;
  wasEdited: boolean;
  title?: string;
  group?: string;
  displayGroup?: string;
  displayOrder?: number;
  icon?: string;
  aiShortcode?: string;
  aiShortcodeAliases?: string[];
  choiceOptions?: string[];
  awaitingChoice?: boolean;
  hidden?: boolean;
  derivedFrom?: string;
  generatedByParser?: boolean;
}

type SectionData = Record<string, SectionItemData>;

type AdCopyRuntimePlan = {
  active_output_fields?: unknown;
};

interface KnowledgePipelineItem {
  type: string;
  name: string;
  title?: string;
  group?: string | null;
  displayGroup?: string | null;
  displayOrder?: number | null;
  icon?: string;
}

const AD_COPY_VISIBLE_OUTPUT_FIELDS = new Set([
  'ad_copy_tofu_aida',
  'ad_copy_tofu_pas',
  'ad_copy_tofu_story_social_proof',
  'ad_copy_tofu_authority_mechanism',
  'ad_copy_tofu_quiz_myth_buster',
  'ad_copy_tofu_short_benefit',
  'ad_copy_tofu_unaware',
  'ad_copy_tofu_headline_1',
  'ad_copy_tofu_headline_2',
  'ad_copy_tofu_headline_3',
  'ad_copy_tofu_subheadline_1',
  'ad_copy_tofu_subheadline_2',
  'ad_copy_tofu_subheadline_3',
  'ad_copy_tofu_primary_text_1',
  'ad_copy_tofu_primary_text_2',
  'ad_copy_tofu_primary_text_3',
  'ad_copy_tofu_cta',
  'ad_copy_mofu_the_switch',
  'ad_copy_mofu_failed_alternatives',
  'ad_copy_mofu_expert_founder_story',
  'ad_copy_mofu_listicle',
  'ad_copy_mofu_the_secret',
  'ad_copy_mofu_before_after',
  'ad_copy_mofu_faq_objection_killer',
  'ad_copy_mofu_headline_1',
  'ad_copy_mofu_headline_2',
  'ad_copy_mofu_headline_3',
  'ad_copy_mofu_subheadline_1',
  'ad_copy_mofu_subheadline_2',
  'ad_copy_mofu_subheadline_3',
  'ad_copy_mofu_primary_text_1',
  'ad_copy_mofu_primary_text_2',
  'ad_copy_mofu_primary_text_3',
  'ad_copy_mofu_cta',
  'ad_copy_bofu_direct_offer',
  'ad_copy_bofu_pain_reminder_solution',
  'ad_copy_bofu_social_proof_scarcity',
  'ad_copy_bofu_risk_reversal',
  'ad_copy_bofu_bundle_stack',
  'ad_copy_bofu_last_push',
  'ad_copy_bofu_headline_1',
  'ad_copy_bofu_headline_2',
  'ad_copy_bofu_headline_3',
  'ad_copy_bofu_subheadline_1',
  'ad_copy_bofu_subheadline_2',
  'ad_copy_bofu_subheadline_3',
  'ad_copy_bofu_primary_text_1',
  'ad_copy_bofu_primary_text_2',
  'ad_copy_bofu_primary_text_3',
  'ad_copy_bofu_cta',
  'ad_copy_variant_1_body',
  'ad_copy_variant_1_headline',
  'ad_copy_variant_1_subheadline',
  'ad_copy_variant_1_primary_text',
  'ad_copy_variant_1_test_variable',
  'ad_copy_variant_2_body',
  'ad_copy_variant_2_headline',
  'ad_copy_variant_2_subheadline',
  'ad_copy_variant_2_primary_text',
  'ad_copy_variant_2_test_variable',
  'ad_copy_variant_3_body',
  'ad_copy_variant_3_headline',
  'ad_copy_variant_3_subheadline',
  'ad_copy_variant_3_primary_text',
  'ad_copy_variant_3_test_variable',
]);

const AD_COPY_FIELD_TITLES: Record<string, string> = {
  ad_copy_tofu_aida: 'TOFU - AIDA',
  ad_copy_tofu_pas: 'TOFU - PAS',
  ad_copy_tofu_story_social_proof: 'TOFU - Story / Social Proof',
  ad_copy_tofu_authority_mechanism: 'TOFU - Authority / Mechanism',
  ad_copy_tofu_quiz_myth_buster: 'TOFU - Quiz / Myth Buster',
  ad_copy_tofu_short_benefit: 'TOFU - Short Benefit',
  ad_copy_tofu_unaware: 'TOFU - Unaware',
};

const KB_GROUP_ORDER = ['Produkt', 'Konkurencja', 'Persona', 'Oferta', 'PODSUMOWANIE'];
const KB_FALLBACK_GROUP = 'Inne';
const KB_GROUP_RENAMES: Record<string, string> = {
  Finanse: 'Oferta',
  FINANSE: 'Oferta',
};
const KB_SPECIAL_DISPLAY_ORDER: Record<string, number> = {
  brand_name_options: 0,
  knowledge_base_coherence_audit: 10000,
};

interface GeneratedBuilderPage {
  page: { id: string; title: string; status: string };
  resourceType?: 'page' | 'product';
  updateScope?: BuilderPageUpdateScope;
  wcProductId?: number;
  productEditorUrl?: string;
  productBuilderUrl?: string;
  builderUrl: string;
  replacedShortcodes: string[];
  missingShortcodes: string[];
}

type BuilderPageUpdateScope = 'all' | 'content' | 'graphics';
type BuilderPageType = 'ADVERTORIAL' | 'STANDALONE' | 'PRODUCT_PAGE' | 'QUIZ';

interface BuilderPageConfig {
  [key: string]: unknown;
  pageType: BuilderPageType;
  templateId?: string | null;
  templateContent?: unknown[] | null;
  templateName?: string | null;
  productTitleTemplate?: string | null;
  productShortDescriptionTemplate?: string | null;
  productShortDescriptionBuilder?: unknown[] | null;
  productAdditionalElements?: unknown[] | null;
}

const BUILDER_PAGE_TYPE_LABELS: Record<BuilderPageType, string> = {
  ADVERTORIAL: 'Advertorial',
  STANDALONE: 'Strona',
  PRODUCT_PAGE: 'Strona produktowa',
  QUIZ: 'Quiz',
};

function isProductBuilderConfig(config: BuilderPageConfig | null): boolean {
  return config?.pageType === 'PRODUCT_PAGE';
}

function hasBuilderTemplate(config: BuilderPageConfig | null): boolean {
  return config?.pageType === 'QUIZ' || Boolean(config?.templateId || (Array.isArray(config?.templateContent) && config.templateContent.length > 0));
}

const CONTENT_MENU_GROUPS = [
  { label: 'STRONY', types: ['ADVERTORIAL', 'LISTICALE', 'QUIZ', 'LANDING'] },
  { label: 'REKLAMA', types: ['AD_COPY', 'VIDEO_SCRIPT', 'AD_GRAPHIC'] },
  { label: 'SOCIAL MEDIA', types: ['SOCIAL_POST'] },
];

const VIDEO_AD_CONTENT_TYPES = new Set(['VIDEO_SCRIPT', 'EXPLAINER', 'VSL', 'VSL_STORY', 'HOOKS', 'UGC_SCRIPT']);

const CONTENT_TYPE_ALIASES: Record<string, string[]> = {
  // Zakładka Reklamy wideo pokazuje też legacy treści z okresu osobnych zakładek per strategia
  VIDEO_SCRIPT: ['VIDEO_SCRIPT', 'EXPLAINER', 'VSL', 'VSL_STORY', 'HOOKS', 'UGC_SCRIPT'],
  AD_GRAPHIC: ['AD_GRAPHIC', 'AD_CREATIVE'],
};

const GENERATABLE_CONTENT_TYPES = new Set([
  'LANDING',
  'LISTICALE',
  'ADVERTORIAL',
  'QUIZ',
  'VIDEO_SCRIPT',
  'EXPLAINER',
  'VSL',
  'VSL_STORY',
  'HOOKS',
  'AD_COPY',
  'AD_GRAPHIC',
  'AD_CREATIVE',
  'UGC_SCRIPT',
  'SOCIAL_POST',
]);

type FunnelStage = 'TOFU' | 'MOFU' | 'BOFU';

const FUNNEL_STAGES: Array<{ value: FunnelStage; label: string }> = [
  { value: 'TOFU', label: 'TOFU' },
  { value: 'MOFU', label: 'MOFU' },
  { value: 'BOFU', label: 'BOFU' },
];

const FUNNEL_CONTENT_TYPES = new Set(['AD_COPY', 'AD_GRAPHIC']);

function contentTypesForMenuType(type: string): string[] {
  return CONTENT_TYPE_ALIASES[type] || [type];
}

function canGenerateContentType(type: string): boolean {
  return GENERATABLE_CONTENT_TYPES.has(type);
}

const PRODUCT_PAGE_SOURCE_ORDER = new Map<string, number>([
  ['product_title', 1000],
  ['main_marketing_promise', 2000],
  ['short_product_description', 3000],
  ['bullet_points', 4000],
  ['short_cta_review', 5000],
  ['pas_section_heading', 5990],
  ['pas_section', 6000],
  ['emotional_opening_heading', 6990],
  ['emotional_opening', 7000],
  ['customer_reviews_carousel', 8000],
  ['unique_action_mechanism_heading', 8990],
  ['unique_action_mechanism', 9000],
  ['feature_benefit_value_module_1_heading', 10005],
  ['feature_benefit_value_module_2_heading', 10015],
  ['feature_benefit_value_modules', 10000],
  ['how_to_use_section_heading', 10990],
  ['how_to_use_section', 11000],
  ['three_customer_reviews', 12000],
  ['false_dichotomy_section', 13000],
  ['authority_review', 14000],
  ['satisfaction_guarantee', 15000],
  ['faq_section', 16000],
  ['pricing_packages_section', 17000],
]);

const PRODUCT_PAGE_SPLIT_SOURCE_FIELDS = new Set([
  'bullet_points',
  'short_cta_review',
  'pricing_packages_section',
  'emotional_opening',
  'customer_reviews_carousel',
  'unique_action_mechanism',
  'feature_benefit_value_modules',
  'how_to_use_section',
  'three_customer_reviews',
  'false_dichotomy_section',
  'authority_review',
  'satisfaction_guarantee',
  'faq_section',
]);

const PRODUCT_PAGE_FIELD_TITLES: Record<string, string> = {
  product_title: 'Tytuł produktu',
  main_marketing_promise: 'Główna obietnica marketingowa',
  short_product_description: 'Krótki opis produktu',
  bullet_points: 'Bullet points',
  short_cta_review: 'Krótka opinia pod CTA',
  pricing_packages_section: 'Pakiety cenowe',
  pas_section: 'Sekcja PAS',
  pas_section_heading: 'Sekcja PAS - nagłówek',
  emotional_opening: 'Emocjonalne otwarcie',
  emotional_opening_heading: 'Emocjonalne otwarcie - nagłówek',
  customer_reviews_carousel: 'Karuzela opinii klientów',
  unique_action_mechanism: 'Unikalny mechanizm działania',
  unique_action_mechanism_heading: 'Unikalny mechanizm działania - nagłówek',
  feature_benefit_value_modules: 'Moduły funkcja - korzyść - wartość',
  feature_benefit_value_module_1_heading: 'Moduł FBV 1 - nagłówek',
  feature_benefit_value_module_2_heading: 'Moduł FBV 2 - nagłówek',
  how_to_use_section: 'Instrukcja zastosowania',
  how_to_use_section_heading: 'Instrukcja zastosowania - nagłówek',
  three_customer_reviews: 'Opinie klientów do kart',
  false_dichotomy_section: 'Porównanie alternatyw',
  authority_review: 'Opinia autorytetu',
  satisfaction_guarantee: 'Gwarancja satysfakcji',
  faq_section: 'FAQ',
};

function hasDerivedContentSections(sections: SectionData, sourceFieldName: string): boolean {
  return Object.values(sections).some(section => section?.generatedByParser && section.derivedFrom === sourceFieldName);
}

function isTechnicalProductPageSlot(fieldName: string): boolean {
  return /^bullet_point_\d+_(icon|text)$/.test(fieldName)
    || /^false_dichotomy_feature_\d+_color_\d+$/.test(fieldName)
    || fieldName === 'authority_review_quote';
}

function getAdCopyActiveOutputFields(sections: SectionData): Set<string> | null {
  const content = (sections.ad_copy_runtime_plan?.edited || sections.ad_copy_runtime_plan?.original || '').trim();
  if (!content) return null;
  try {
    const parsed = JSON.parse(content) as AdCopyRuntimePlan;
    if (!Array.isArray(parsed.active_output_fields)) return null;
    const fields = parsed.active_output_fields
      .filter((field): field is string => typeof field === 'string' && field.trim().length > 0)
      .map(field => field.trim());
    return fields.length > 0 ? new Set(fields) : null;
  } catch {
    return null;
  }
}

function isVisibleAdCopyOutputSection(
  fieldName: string,
  section: SectionItemData,
  activeOutputFields: Set<string> | null,
): boolean {
  if (!AD_COPY_VISIBLE_OUTPUT_FIELDS.has(fieldName)) return false;
  if (activeOutputFields && !activeOutputFields.has(fieldName)) return false;
  const content = (section.edited || section.original || '').trim();
  return Boolean(content) && content !== 'NIE_DOTYCZY';
}

function shouldDisplayContentSection(
  fieldName: string,
  section: SectionItemData,
  sections: SectionData,
  activeAdCopyOutputFields: Set<string> | null,
): boolean {
  if (!section || fieldName.startsWith('__')) return false;
  if (isTechnicalProductPageSlot(fieldName)) return false;
  if (PRODUCT_PAGE_SPLIT_SOURCE_FIELDS.has(fieldName) && hasDerivedContentSections(sections, fieldName)) return false;
  if (AD_COPY_VISIBLE_OUTPUT_FIELDS.has(fieldName)) {
    return isVisibleAdCopyOutputSection(fieldName, section, activeAdCopyOutputFields);
  }
  if (section.generatedByParser) return true;
  return !section.hidden;
}

function derivedProductPageSlotOffset(fieldName: string, fallbackIndex: number): number {
  const bullet = fieldName.match(/^bullet_point_(\d+)$/);
  if (bullet) return Number(bullet[1]) * 10;

  if (fieldName.endsWith('_intro')) return 10;
  if (fieldName.endsWith('_closing')) return 900;

  const numberedPair = fieldName.match(/_(\d+)_(author|text|question|answer|title|icon)$/);
  if (numberedPair) {
    const itemNo = Number(numberedPair[1]);
    const partOrder: Record<string, number> = {
      author: 1,
      text: 2,
      question: 1,
      answer: 2,
      title: 1,
      icon: 3,
    };
    return itemNo * 10 + (partOrder[numberedPair[2]] ?? 0);
  }

  if (fieldName.endsWith('_author')) return 10;
  if (fieldName.endsWith('_rank')) return 20;
  if (fieldName.endsWith('_text')) return 30;
  if (fieldName.endsWith('_heading')) return 10;
  if (fieldName.endsWith('_subheading')) return 20;

  const pricingPackage = fieldName.match(/^pricing_package_(\d+)_(badge|title|subtitle|old_price|price|feature_(\d+))$/);
  if (pricingPackage) {
    const itemNo = Number(pricingPackage[1]);
    const part = pricingPackage[2];
    const partOrder: Record<string, number> = {
      badge: 1,
      title: 2,
      subtitle: 3,
      old_price: 4,
      price: 5,
    };
    const featureNo = pricingPackage[3] ? Number(pricingPackage[3]) : 0;
    return itemNo * 100 + (part.startsWith('feature_') ? 20 + featureNo : partOrder[part] ?? 0);
  }

  const falseDichotomyAlt = fieldName.match(/^false_dichotomy_alternative_(\d+)_name$/);
  if (falseDichotomyAlt) return 100 + Number(falseDichotomyAlt[1]) * 10;

  const falseDichotomyFeature = fieldName.match(/^false_dichotomy_feature_(\d+)_(label|value_(\d+))$/);
  if (falseDichotomyFeature) {
    const featureNo = Number(falseDichotomyFeature[1]);
    if (falseDichotomyFeature[2] === 'label') return 200 + featureNo * 100;
    return 200 + featureNo * 100 + Number(falseDichotomyFeature[3] || 0) * 10;
  }

  const authorityBenefit = fieldName.match(/^authority_review_benefit_(\d+)$/);
  if (authorityBenefit) return 100 + Number(authorityBenefit[1]) * 10;

  const numberedModule = fieldName.match(/_(\d+)$/);
  if (numberedModule) return Number(numberedModule[1]) * 10;

  return fallbackIndex + 1;
}

function contentSectionOrder(fieldName: string, section: SectionItemData, fallbackIndex: number): number {
  if (typeof section.displayOrder === 'number') return section.displayOrder;
  const sourceOrder = PRODUCT_PAGE_SOURCE_ORDER.get(fieldName);
  if (typeof sourceOrder === 'number') return sourceOrder;
  if (section.derivedFrom) {
    const derivedSourceOrder = PRODUCT_PAGE_SOURCE_ORDER.get(section.derivedFrom);
    if (typeof derivedSourceOrder === 'number') {
      return derivedSourceOrder + derivedProductPageSlotOffset(fieldName, fallbackIndex);
    }
  }
  return 900000 + fallbackIndex;
}

function getDisplayContentSectionEntries(sections: SectionData): Array<[string, SectionItemData]> {
  const activeAdCopyOutputFields = getAdCopyActiveOutputFields(sections);
  return Object.entries(sections)
    .map(([fieldName, section], index) => ({ fieldName, section, index }))
    .filter(({ fieldName, section }) => shouldDisplayContentSection(fieldName, section, sections, activeAdCopyOutputFields))
    .sort((a, b) => {
      const byOrder = contentSectionOrder(a.fieldName, a.section, a.index) - contentSectionOrder(b.fieldName, b.section, b.index);
      if (byOrder !== 0) return byOrder;
      return a.index - b.index;
    })
    .map(({ fieldName, section }) => [fieldName, section]);
}

function getProductPageDerivedTitle(fieldName: string): string | null {
  const bullet = fieldName.match(/^bullet_point_(\d+)$/);
  if (bullet) return `Bullet ${bullet[1]}`;

  const shortCta = fieldName.match(/^short_cta_review_(author|text)$/);
  if (shortCta) return `Opinia pod CTA - ${shortCta[1] === 'author' ? 'autor' : 'treść'}`;

  if (fieldName === 'emotional_opening_intro') return 'Emocjonalne otwarcie - wstęp';
  const emotional = fieldName.match(/^emotional_opening_benefit_(\d+)_(title|text|icon)$/);
  if (emotional) return `Emocjonalne otwarcie ${emotional[1]} - ${partLabel(emotional[2])}`;

  if (fieldName === 'unique_action_mechanism_intro') return 'Unikalny mechanizm - wstęp';
  const mechanism = fieldName.match(/^unique_action_mechanism_point_(\d+)_(title|text|icon)$/);
  if (mechanism) return `Mechanizm ${mechanism[1]} - ${partLabel(mechanism[2])}`;

  if (fieldName === 'how_to_use_intro') return 'Instrukcja użycia - wstęp';
  const step = fieldName.match(/^how_to_use_step_(\d+)_(title|text|icon)$/);
  if (step) return `Krok ${step[1]} - ${partLabel(step[2])}`;

  const carouselReview = fieldName.match(/^customer_review_(\d+)_(author|text)$/);
  if (carouselReview) return `Opinia karuzeli ${carouselReview[1]} - ${partLabel(carouselReview[2])}`;

  const cardReview = fieldName.match(/^three_customer_review_(\d+)_(author|text)$/);
  if (cardReview) return `Opinia karty ${cardReview[1]} - ${partLabel(cardReview[2])}`;

  const module = fieldName.match(/^feature_benefit_value_module_(\d+)$/);
  if (module) return `Moduł funkcja - korzyść - wartość ${module[1]}`;

  if (fieldName === 'false_dichotomy_heading') return 'Porównanie - nagłówek';
  if (fieldName === 'false_dichotomy_subheading') return 'Porównanie - podnagłówek';
  if (fieldName === 'false_dichotomy_closing') return 'Porównanie - domknięcie';
  const alternative = fieldName.match(/^false_dichotomy_alternative_(\d+)_name$/);
  if (alternative) return `Alternatywa ${alternative[1]}`;
  const featureLabel = fieldName.match(/^false_dichotomy_feature_(\d+)_label$/);
  if (featureLabel) return `Cecha porównania ${featureLabel[1]}`;
  const featureValue = fieldName.match(/^false_dichotomy_feature_(\d+)_value_(\d+)$/);
  if (featureValue) return `Cecha ${featureValue[1]} - wartość alternatywy ${featureValue[2]}`;

  const authority = fieldName.match(/^authority_review_(author|rank|text)$/);
  if (authority) return `Opinia autorytetu - ${partLabel(authority[1])}`;
  const authorityBenefit = fieldName.match(/^authority_review_benefit_(\d+)$/);
  if (authorityBenefit) return `Benefit autorytetu ${authorityBenefit[1]}`;

  if (fieldName === 'satisfaction_guarantee_heading') return 'Gwarancja - nagłówek';
  if (fieldName === 'satisfaction_guarantee_text') return 'Gwarancja - treść';

  const faq = fieldName.match(/^faq_(\d+)_(question|answer)$/);
  if (faq) return `FAQ ${faq[1]} - ${faq[2] === 'question' ? 'pytanie' : 'odpowiedź'}`;

  return null;
}

function partLabel(part: string): string {
  if (part === 'author') return 'autor';
  if (part === 'rank') return 'ranga';
  if (part === 'text') return 'treść';
  if (part === 'title') return 'nagłówek';
  if (part === 'icon') return 'ikona';
  return part;
}

function getContentSectionTitle(fieldName: string, section: SectionItemData): string {
  return getProductPageDerivedTitle(fieldName)
    || PRODUCT_PAGE_FIELD_TITLES[fieldName]
    || AD_COPY_FIELD_TITLES[fieldName]
    || section.title
    || FIELD_TITLES[fieldName]
    || fieldName.replace(/_/g, ' ');
}

function copyShortcodeToClipboard(shortcode: string, setCopiedId: (value: string | null) => void) {
  const value = `[${shortcode}]`;
  navigator.clipboard.writeText(value);
  setCopiedId(`shortcode:${shortcode}`);
  setTimeout(() => setCopiedId(null), 2000);
}

/* ─── Helper: build full text of a content item ─── */
function buildContentText(content: LabContent): string {
  const sections = (content.sections || {}) as SectionData;
  return getDisplayContentSectionEntries(sections)
    .map(([key, section]) => {
      const title = getContentSectionTitle(key, section);
      const text = section.edited || section.original || '';
      return `## ${title}\n\n${text}`;
    })
    .join('\n\n---\n\n');
}

/* ─── Helper: download text as .md ─── */
function downloadMd(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildKnowledgeMetaMap(items: KnowledgePipelineItem[]): Map<string, KnowledgePipelineItem> {
  return new Map(
    items
      .filter(item => item.type === 'field' || item.type === 'choice' || item.type === 'image_gen')
      .map(item => [item.name, item]),
  );
}

function orderKnowledgeFields(sections: SectionData, items: KnowledgePipelineItem[]): string[] {
  const sectionNames = new Set(Object.keys(sections));
  const orderByName = new Map(items.map((item, index) => [item.name, index]));
  const metaByName = buildKnowledgeMetaMap(items);
  const displayOrderFor = (name: string): number => {
    const section = sections[name];
    const meta = metaByName.get(name);
    if (typeof section?.displayOrder === 'number') return section.displayOrder;
    if (typeof meta?.displayOrder === 'number') return meta.displayOrder;
    if (typeof KB_SPECIAL_DISPLAY_ORDER[name] === 'number') return KB_SPECIAL_DISPLAY_ORDER[name];
    return (orderByName.get(name) ?? 9999) + 100;
  };

  const ordered = items
    .filter(item => sectionNames.has(item.name))
    .map(item => item.name)
    .sort((a, b) => {
      const byDisplay = displayOrderFor(a) - displayOrderFor(b);
      if (byDisplay !== 0) return byDisplay;
      return (orderByName.get(a) ?? 9999) - (orderByName.get(b) ?? 9999);
    });
  const configuredNames = new Set(ordered);
  const remaining = Object.keys(sections).filter(name => !configuredNames.has(name));
  return [...ordered, ...remaining];
}

function getKbSectionTitle(fieldName: string, section: SectionItemData, meta?: KnowledgePipelineItem): string {
  const override = KB_SECTION_TITLE_OVERRIDES[fieldName];
  if (override) return override;
  return section.title || meta?.title || FIELD_TITLES[fieldName] || fieldName.replace(/_/g, ' ');
}

function getKbSectionGroup(section: SectionItemData, meta?: KnowledgePipelineItem): string {
  const group = section.displayGroup || meta?.displayGroup || section.group || meta?.group || KB_FALLBACK_GROUP;
  return KB_GROUP_RENAMES[group] || group;
}

function normalizeHeadingText(value: string): string {
  return value
    .replace(/[`*_~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function stripLeadingDuplicateHeading(text: string, title: string): string {
  const trimmed = text.trimStart();
  const match = trimmed.match(/^#{1,6}\s+(.+?)\s*\n+/);
  if (!match) return text;

  const heading = normalizeHeadingText(match[1]);
  const normalizedTitle = normalizeHeadingText(title);
  if (!heading || heading !== normalizedTitle) return text;

  return trimmed.slice(match[0].length).trimStart();
}

function stripKbSectionHeading(
  text: string,
  fieldName: string,
  section: SectionItemData,
  meta: KnowledgePipelineItem | undefined,
  displayTitle: string,
): string {
  const legacyTitle = section.title || meta?.title || FIELD_TITLES[fieldName] || fieldName.replace(/_/g, ' ');
  const strippedByDisplayTitle = stripLeadingDuplicateHeading(text, displayTitle);
  if (strippedByDisplayTitle !== text || legacyTitle === displayTitle) return strippedByDisplayTitle;
  return stripLeadingDuplicateHeading(text, legacyTitle);
}

function countVisibleSections(sections?: Record<string, unknown> | null): number {
  if (!sections) return 0;
  return Object.entries(sections).filter(([fieldName, section]) => !isKbSectionHidden(fieldName, section)).length;
}

function isKbSectionHidden(fieldName: string, section?: unknown): boolean {
  if (HIDDEN_KB_SECTION_FIELDS.has(fieldName)) return true;
  if (!section || typeof section !== 'object') return false;
  return Boolean((section as { hidden?: boolean }).hidden);
}

/* ─── Helper: build full KB text ─── */
function buildKbText(sections: SectionData, pipelineItems: KnowledgePipelineItem[] = []): string {
  const metaByName = buildKnowledgeMetaMap(pipelineItems);
  const orderedFields = orderKnowledgeFields(sections, pipelineItems).filter(fieldName => !isKbSectionHidden(fieldName, sections[fieldName]));
  const chunks: string[] = [];
  let currentGroup = '';

  for (const fieldName of orderedFields) {
    const section = sections[fieldName];
    if (!section) continue;
    const meta = metaByName.get(fieldName);
    const group = getKbSectionGroup(section, meta);
    const title = getKbSectionTitle(fieldName, section, meta);
    const text = stripKbSectionHeading(section.edited || section.original || '', fieldName, section, meta, title);

    if (group !== currentGroup) {
      chunks.push(`# ${group}`);
      currentGroup = group;
    }
    chunks.push(`## ${title}\n\n${text}`);
  }

  return chunks.join('\n\n---\n\n');
}

/* ─── Default content title ─── */
function getDefaultTitle(productName: string, contentType: string, version: number): string {
  const typeLabel = CONTENT_TYPE_LABEL[contentType] || contentType;
  return `${productName} - ${typeLabel} v.${version}`;
}

/* ─── Format date ─── */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pl-PL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string; message?: string } } }).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

/* ─── Menu item type ─── */
type MenuTab =
  | { kind: 'kb-list' }
  | { kind: 'kb-detail'; kbId: string; kb: KnowledgeBase }
  | { kind: 'reference-images' }
  | { kind: 'video-bank' }
  | { kind: 'content'; type: string };

const CONTENT_HUB_KB_TAB = 'kb';
const CONTENT_HUB_REFERENCE_IMAGES_TAB = 'reference-images';
const CONTENT_HUB_VIDEO_BANK_TAB = 'video-bank';
const IMPORTABLE_PAGE_PIPELINES = new Set(['ADVERTORIAL', 'LISTICALE', 'QUIZ', 'LANDING']);

function isContentHubContentType(value: string | null | undefined): value is string {
  return Boolean(value && CONTENT_SECTIONS.some(section => section.type === value));
}

function resolveContentHubTab(
  token: string | null | undefined,
  product: LabProduct,
  hasCompleteKb: boolean,
): MenuTab | null {
  if (token?.startsWith('kb:')) {
    const kbId = token.slice(3);
    const kb = product.knowledgeBases.find(item => item.id === kbId);
    if (kb) return { kind: 'kb-detail', kbId: kb.id, kb };
  }
  if (isContentHubContentType(token) && hasCompleteKb) return { kind: 'content', type: token };
  if (token === CONTENT_HUB_REFERENCE_IMAGES_TAB && hasCompleteKb) return { kind: 'reference-images' };
  if (token === CONTENT_HUB_VIDEO_BANK_TAB && hasCompleteKb) return { kind: 'video-bank' };
  if (token === CONTENT_HUB_KB_TAB && product.knowledgeBases.length > 0) return { kind: 'kb-list' };
  return null;
}

function contentHubTabToken(tab: MenuTab | null): string | null {
  if (!tab) return null;
  if (tab.kind === 'kb-list') return CONTENT_HUB_KB_TAB;
  if (tab.kind === 'kb-detail') return `kb:${tab.kbId}`;
  if (tab.kind === 'reference-images') return CONTENT_HUB_REFERENCE_IMAGES_TAB;
  if (tab.kind === 'video-bank') return CONTENT_HUB_VIDEO_BANK_TAB;
  return tab.type;
}

/* ═══════════════════════════════════════════════════════ */

interface ContentHubProps {
  product: LabProduct;
  productId: string;
  hasCompleteKb: boolean;
  generatingContent: string | null;
  onNewContent: (type: string, initialInputs?: Record<string, string>) => void;
  onNewKb: () => void;
  onDeleteKb: (kbId: string) => void;
  onDeleteContent: (contentId: string) => void;
  onResumeContent: (content: LabContent) => void;
  onResumeKnowledge: (kbId: string) => Promise<void>;
  resumingKnowledge: boolean;
  knowledgeGenerationError: string | null;
  onReload: () => void;
  onOpenBatchModal: () => void;
  onGenerateProductImages: (data: {
    labContentId: string;
    knowledgeBaseId?: string;
    language: string;
    styleId?: string;
    quality?: 'low' | 'medium' | 'high';
    regenerateImageIds?: string[];
  }) => Promise<void> | void;
  initialTabToken?: string | null;
  onTabTokenChange?: (token: string | null) => void;
}

export function ContentHub({
  product,
  productId,
  hasCompleteKb,
  generatingContent,
  onNewContent,
  onNewKb,
  onDeleteKb,
  onDeleteContent,
  onResumeContent,
  onResumeKnowledge,
  resumingKnowledge,
  knowledgeGenerationError,
  onReload,
  onOpenBatchModal,
  onGenerateProductImages,
  initialTabToken,
  onTabTokenChange,
}: ContentHubProps) {
  const confirm = useConfirmDialog();
  const [replaceTarget, setReplaceTarget] = useState<{ id: string; kind: 'knowledge' | 'content' } | null>(null);

  /* ─── State ─── */
  const [activeTab, setActiveTab] = useState<MenuTab | null>(() => (
    resolveContentHubTab(initialTabToken, product, hasCompleteKb)
  ));

  // KB inline state
  const [kbSections, setKbSections] = useState<SectionData>({});
  const [kbSectionCounts, setKbSectionCounts] = useState<Record<string, number>>({});
  const [kbPipelineItems, setKbPipelineItems] = useState<KnowledgePipelineItem[]>([]);
  const [loadingKb, setLoadingKb] = useState(false);
  const [kbEditingField, setKbEditingField] = useState<string | null>(null);
  const [kbEditValue, setKbEditValue] = useState('');
  const [kbRegenerating, setKbRegenerating] = useState<string | null>(null);
  const [kbSaving, setKbSaving] = useState(false);
  const [expandedKbSections, setExpandedKbSections] = useState<Record<string, boolean>>({});

  // Content inline state
  const [expandedContent, setExpandedContent] = useState<string | null>(null);
  const [expandedContentSections, setExpandedContentSections] = useState<Record<string, boolean>>({});
  const [editingField, setEditingField] = useState<{ contentId: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeFunnelStageByType, setActiveFunnelStageByType] = useState<Record<string, FunnelStage>>({
    AD_COPY: 'TOFU',
    AD_GRAPHIC: 'TOFU',
  });
  const [iterateVariantContent, setIterateVariantContent] = useState<LabContent | null>(null);
  const [campaignContent, setCampaignContent] = useState<LabContent | null>(null);
  const [contentSourceModal, setContentSourceModal] = useState<{
    pipeline: 'ADVERTORIAL' | 'LISTICALE' | 'QUIZ' | 'LANDING';
    initialInputs?: Record<string, string>;
  } | null>(null);
  const [sendingToStudioId, setSendingToStudioId] = useState<string | null>(null);

  // "Wyślij do Studio" - paczka reklamy wideo (skrypt + zasoby + pliki) jako nowy projekt montażu
  const handleSendToStudio = async (content: LabContent) => {
    setSendingToStudioId(content.id);
    try {
      const res = await studioApi.sendVideoPackage({ labProductId: productId, contentId: content.id });
      const projectId = res.data.data?.project?.id;
      if (projectId) window.open(`/studio/${projectId}`, '_blank');
    } catch (err) {
      console.error('Failed to send video package to Studio:', err);
    } finally {
      setSendingToStudioId(null);
    }
  };

  // Reference images state
  const [referenceImages, setReferenceImages] = useState<LabReferenceImage[]>(product.referenceImages || []);
  const [loadingReferenceImages, setLoadingReferenceImages] = useState(false);
  const [generatingReferenceImages, setGeneratingReferenceImages] = useState(false);
  const [draggingReferenceScreenshots, setDraggingReferenceScreenshots] = useState(false);
  const [uploadingReferenceKind, setUploadingReferenceKind] = useState<LabReferenceImageKind | null>(null);
  const [referenceImageMessage, setReferenceImageMessage] = useState<string | null>(null);
  const [referenceImageError, setReferenceImageError] = useState<string | null>(null);
  const [referenceScreenshotFiles, setReferenceScreenshotFiles] = useState<File[]>([]);
  const [showReferenceBatchConfigurator, setShowReferenceBatchConfigurator] = useState(false);
  const [referenceTypographyModal, setReferenceTypographyModal] = useState<{ image: LabReferenceImage | null } | null>(null);
  const [referenceImageToRegenerate, setReferenceImageToRegenerate] = useState<LabReferenceImage | null>(null);
  const [referenceRegenerationInstruction, setReferenceRegenerationInstruction] = useState('');
  const [referenceTypographyStyle, setReferenceTypographyStyle] = useState(TYPOGRAPHY_STYLE_OPTIONS[0].id);
  const [referenceTypographyMode, setReferenceTypographyMode] = useState<'style' | 'prompt'>('style');
  const [regeneratingReferenceImageId, setRegeneratingReferenceImageId] = useState<string | null>(null);
  const [deletingReferenceImageId, setDeletingReferenceImageId] = useState<string | null>(null);
  const [referenceGenerationRunId, setReferenceGenerationRunId] = useState<string | null>(null);
  const [referenceGenerationTargetKinds, setReferenceGenerationTargetKinds] = useState<LabReferenceImageKind[]>([]);
  const [referenceGenerationProgress, setReferenceGenerationProgress] = useState({
    current: 0,
    total: 0,
    generated: 0,
    message: '',
    status: 'idle' as 'idle' | 'generating' | 'completed' | 'error' | 'cancelled',
  });
  const [stoppingReferenceImages, setStoppingReferenceImages] = useState(false);
  const [generatingBundleKind, setGeneratingBundleKind] = useState<LabReferenceImageKind | null>(null);
  const [bundleConfigurator, setBundleConfigurator] = useState<{ kind: LabReferenceImageKind; file?: File } | null>(null);
  const initialStyleId = typeof product.customFields?.productImageStyleId === 'string' ? product.customFields.productImageStyleId : '';
  const [savedProductImageStyleId, setSavedProductImageStyleId] = useState(initialStyleId);
  const [referenceStyleSelection, setReferenceStyleId] = useState(initialStyleId);
  const [referenceStylePhoto, setReferenceStylePhoto] = useState<File | null>(null);
  const [savingReferenceStyle, setSavingReferenceStyle] = useState(false);
  const [customBundleQuantity, setCustomBundleQuantity] = useState('');

  // Product images state
  const [productImages, setProductImages] = useState<LabProductImage[]>(product.productImages || []);
  const [loadingProductImages, setLoadingProductImages] = useState(false);
  const [productImageError, setProductImageError] = useState<string | null>(null);
  const productImageUploadInput = useRef<HTMLInputElement>(null);
  const productImageUploadTarget = useRef<string | null>(null);
  const [uploadingProductImageId, setUploadingProductImageId] = useState<string | null>(null);
  const [preparingImageSlotsId, setPreparingImageSlotsId] = useState<string | null>(null);
  const [productImagesGenerateModal, setProductImagesGenerateModal] = useState<LabContent | null>(null);
  const [productImageGenerationLanguage, setProductImageGenerationLanguage] = useState('pl');
  const [productImageStyleSelection, setProductImageStyleId] = useState(initialStyleId);
  const productImageStylesQuery = useQuery({
    queryKey: ['lab', 'product-image-styles'],
    queryFn: async () => {
      const res = await labApi.getProductImageStyles();
      return ((res.data.data?.cards || []) as ProductImageStyleCard[]).filter(card => card.isActive !== false);
    },
    enabled: Boolean(productImagesGenerateModal || showReferenceBatchConfigurator),
    staleTime: 60_000,
  });
  const productImageStyleCards = useMemo(() => productImageStylesQuery.data || [], [productImageStylesQuery.data]);
  const loadingProductImageStyles = productImageStylesQuery.isFetching;
  const referenceStyleId = referenceStyleSelection || productImageStyleCards[0]?.id || '';
  const productImageStyleId = savedProductImageStyleId || productImageStyleSelection || productImageStyleCards[0]?.id || '';
  const [selectedProductImageIds, setSelectedProductImageIds] = useState<string[]>([]);
  const [startingProductImagesGeneration, setStartingProductImagesGeneration] = useState(false);
  const [productImagePromptModal, setProductImagePromptModal] = useState<LabProductImage | null>(null);
  const [productImagePromptValue, setProductImagePromptValue] = useState('');
  const [savingProductImagePrompt, setSavingProductImagePrompt] = useState(false);
  const [productImagePreviewModal, setProductImagePreviewModal] = useState<LabProductImage | null>(null);
  const [productImageEditModal, setProductImageEditModal] = useState<LabProductImage | null>(null);
  const [productImageEditInstruction, setProductImageEditInstruction] = useState('');
  const [productImageEditMode, setProductImageEditMode] = useState<'local' | 'reference_rebuild'>('local');
  const [productImageQuality, setProductImageQuality] = useState<'low' | 'medium' | 'high'>('medium');
  const [productImageEditReference, setProductImageEditReference] = useState<File | null>(null);
  const [productImageEditReferenceUrl, setProductImageEditReferenceUrl] = useState('');
  const productImageReferenceInput = useRef<HTMLInputElement>(null);
  const [draggingProductImageReference, setDraggingProductImageReference] = useState(false);
  const acceptProductImageReference = (file?: File) => {
    if (!file || editingProductImageId) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 12 * 1024 * 1024) {
      setProductImageError('Wybierz zdjęcie JPG, PNG lub WEBP do 12 MB.'); return;
    }
    setProductImageError(null);
    setProductImageEditReference(file);
  };
  useEffect(() => {
    if (!productImageEditModal) setProductImageEditReference(null);
  }, [productImageEditModal]);
  useEffect(() => {
    if (!productImageEditReference) { setProductImageEditReferenceUrl(''); return; }
    const url = URL.createObjectURL(productImageEditReference);
    setProductImageEditReferenceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [productImageEditReference]);
  const imageQualityControl = (disabled: boolean) => (
    <label className="block space-y-1.5">
      <span className="text-sm text-text-secondary">Jakość grafiki</span>
      <Select value={productImageQuality} onChange={event => setProductImageQuality(event.target.value as 'low' | 'medium' | 'high')} disabled={disabled}>
        <option value="low">Niska - szybciej i taniej</option>
        <option value="medium">Średnia - więcej szczegółów</option>
        <option value="high">Wysoka - najwięcej szczegółów</option>
      </Select>
      <p className="text-xs text-text-muted">Wyższa jakość zwiększa koszt i może wydłużyć generowanie.</p>
    </label>
  );
  const [editingProductImageId, setEditingProductImageId] = useState<string | null>(null);
  const [regeneratingProductImageId, setRegeneratingProductImageId] = useState<string | null>(null);
  const [deletingProductImageId, setDeletingProductImageId] = useState<string | null>(null);

  // Title editing
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleValue, setTitleValue] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [pageModalContent, setPageModalContent] = useState<LabContent | null>(null);
  const [engineStores, setEngineStores] = useState<Store[]>([]);
  const [builderPageConfig, setBuilderPageConfig] = useState<BuilderPageConfig | null>(null);
  const [pageForm, setPageForm] = useState({
    integrationId: '',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED',
    title: '',
    slug: '',
    compressImages: true,
  });
  const [loadingPageOptions, setLoadingPageOptions] = useState(false);
  const [generatingPage, setGeneratingPage] = useState(false);
  const [pageGenerationError, setPageGenerationError] = useState<string | null>(null);
  const [generatedPage, setGeneratedPage] = useState<GeneratedBuilderPage | null>(null);
  const [brandStoreSelection, setBrandStoreSelection] = useState<{
    integrationId: string | null;
    storeName: string | null;
    locked: boolean;
    warning: string | null;
  }>({ integrationId: null, storeName: null, locked: false, warning: null });
  const [updateModalContent, setUpdateModalContent] = useState<LabContent | null>(null);
  const [updatingPageScope, setUpdatingPageScope] = useState<BuilderPageUpdateScope | null>(null);
  const [pageUpdateError, setPageUpdateError] = useState<string | null>(null);
  const [updatedPage, setUpdatedPage] = useState<GeneratedBuilderPage | null>(null);
  const [pageUpdateProgress, setPageUpdateProgress] = useState({ current: 0, total: 0, message: '' });
  const [compressPageUpdateImages, setCompressPageUpdateImages] = useState(true);

  // Group contents by type
  const contentsByType: Record<string, LabContent[]> = {};
  for (const c of product.contents) {
    if (!contentsByType[c.type]) contentsByType[c.type] = [];
    contentsByType[c.type].push(c);
  }

  const completedKnowledgeBase = useMemo(
    () => product.knowledgeBases.find(kb => kb.isComplete) || null,
    [product.knowledgeBases],
  );
  const referenceImagesByKind = useMemo(() => {
    return new Map(referenceImages.map(image => [image.kind, image]));
  }, [referenceImages]);
  const requiredReferenceImageCount = useMemo(() => {
    return REQUIRED_REFERENCE_IMAGE_ORDER.filter(kind => !(savedProductImageStyleId === PHOTO_STYLE_ID && kind === 'BEFORE_AFTER') && referenceImagesByKind.has(kind)).length;
  }, [referenceImagesByKind, savedProductImageStyleId]);
  const missingReferenceImageKinds = useMemo(
    () => REQUIRED_REFERENCE_IMAGE_ORDER.filter(kind => !referenceImagesByKind.has(kind) && !(kind === 'BEFORE_AFTER' && (showReferenceBatchConfigurator ? referenceStyleId : savedProductImageStyleId) === PHOTO_STYLE_ID)),
    [referenceImagesByKind, referenceStyleId, savedProductImageStyleId, showReferenceBatchConfigurator],
  );
  const hasVisualProductReference = useMemo(
    () => hasProductVisualReference(referenceImagesByKind.keys()),
    [referenceImagesByKind],
  );
  const hasProductStyleReference = referenceImagesByKind.has('PRODUCT_STYLE');
  const productStyleReference = referenceImagesByKind.get('PRODUCT_STYLE') || null;
  const productStyleReferenceImageUrl = productStyleReference
    ? labApi.getReferenceImageFileUrl(productId, productStyleReference.id, productStyleReference.updatedAt || productStyleReference.createdAt)
    : null;
  const productImageStyleOptions = useMemo<ProductImageStyleCardOption[]>(() => {
    return [
      ...productImageStyleCards,
      productStyleReferenceCard({
        hasReference: hasProductStyleReference,
        imageUrl: productStyleReferenceImageUrl,
      }),
    ].filter(card => !savedProductImageStyleId || card.id === savedProductImageStyleId);
  }, [productImageStyleCards, hasProductStyleReference, productStyleReferenceImageUrl, savedProductImageStyleId]);
  const selectableProductImageStyleOptions = useMemo(
    () => productImageStyleOptions.filter(card => !card.isLocked),
    [productImageStyleOptions],
  );
  const productImagesByContentId = useMemo(() => {
    const map = new Map<string, LabProductImage[]>();
    for (const image of productImages) {
      if (!image.labContentId) continue;
      const list = map.get(image.labContentId) || [];
      list.push(image);
      map.set(image.labContentId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.createdAt.localeCompare(b.createdAt));
    }
    return map;
  }, [productImages]);

  const kbMetaByName = useMemo(() => buildKnowledgeMetaMap(kbPipelineItems), [kbPipelineItems]);
  const orderedKbFields = useMemo(
    () => orderKnowledgeFields(kbSections, kbPipelineItems).filter(fieldName => !isKbSectionHidden(fieldName, kbSections[fieldName])),
    [kbSections, kbPipelineItems],
  );
  const groupedKbSections = useMemo(() => {
    const groupMap = new Map<string, string[]>();
    for (const fieldName of orderedKbFields) {
      const section = kbSections[fieldName];
      if (!section) continue;
      const group = getKbSectionGroup(section, kbMetaByName.get(fieldName));
      groupMap.set(group, [...(groupMap.get(group) || []), fieldName]);
    }

    const orderedGroups = [
      ...KB_GROUP_ORDER.filter(group => groupMap.has(group)),
      ...Array.from(groupMap.keys()).filter(group => !KB_GROUP_ORDER.includes(group)),
    ];

    return orderedGroups.map(group => ({
      group,
      fields: groupMap.get(group) || [],
    }));
  }, [orderedKbFields, kbSections, kbMetaByName]);

  // Default to Knowledge Base unless a tab is explicitly requested in the URL.
  useEffect(() => {
    if (!activeTab && product.knowledgeBases.length > 0) {
      setActiveTab({ kind: 'kb-list' });
    }
  }, [activeTab, hasCompleteKb, product, productId]);

  useEffect(() => {
    onTabTokenChange?.(contentHubTabToken(activeTab));
  }, [activeTab, onTabTokenChange]);

  useEffect(() => {
    labApi.getPipeline('KNOWLEDGE').then(res => {
      const items = res.data.data?.items || [];
      setKbPipelineItems(items as KnowledgePipelineItem[]);
    }).catch(err => {
      console.error('Failed to load KB pipeline metadata:', err);
      setKbPipelineItems([]);
    });
  }, []);

  const onReloadRef = useRef(onReload);
  useEffect(() => {
    onReloadRef.current = onReload;
  }, [onReload]);

  const loadReferenceImages = useCallback(async (quiet = false) => {
    if (!quiet) setLoadingReferenceImages(true);
    try {
      const res = await labApi.getReferenceImages(productId);
      setReferenceImages((res.data.data || []) as LabReferenceImage[]);
      setReferenceImageError(null);
      return (res.data.data || []) as LabReferenceImage[];
    } catch (err) {
      const message = apiErrorMessage(err, 'Nie udało się pobrać zdjęć referencyjnych.');
      setReferenceImageError(message);
      return [] as LabReferenceImage[];
    } finally {
      if (!quiet) setLoadingReferenceImages(false);
    }
  }, [productId]);

  useEffect(() => {
    setReferenceImages(product.referenceImages || []);
  }, [product.referenceImages]);

  const loadProductImages = async (quiet = false) => {
    if (!quiet) setLoadingProductImages(true);
    try {
      const res = await labApi.getProductImages(productId);
      setProductImages((res.data.data || []) as LabProductImage[]);
      setProductImageError(null);
      return (res.data.data || []) as LabProductImage[];
    } catch (err) {
      const message = apiErrorMessage(err, 'Nie udało się pobrać zdjęć produktowych.');
      setProductImageError(message);
      return productImages;
    } finally {
      if (!quiet) setLoadingProductImages(false);
    }
  };

  const toggleContent = async (content: LabContent) => {
    if (expandedContent === content.id) { setExpandedContent(null); return; }
    setExpandedContent(content.id);
    if (content.type !== 'LANDING' || preparingImageSlotsId) return;
    setPreparingImageSlotsId(content.id);
    try {
      const response = await labApi.prepareProductImageSlots(productId, content.id);
      setProductImages(current => [
        ...current.filter(image => image.labContentId !== content.id),
        ...(response.data.data as LabProductImage[]),
      ]);
    } catch (error) {
      setProductImageError(apiErrorMessage(error, 'Nie udało się przygotować pól na grafiki.'));
    } finally { setPreparingImageSlotsId(null); }
  };

  const chooseProductImageUpload = (imageId: string) => {
    productImageUploadTarget.current = imageId;
    productImageUploadInput.current?.click();
  };
  const uploadProductImage = async (file?: File) => {
    const imageId = productImageUploadTarget.current;
    if (!file || !imageId || uploadingProductImageId) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 12 * 1024 * 1024) {
      setProductImageError('Wybierz plik JPG, PNG lub WEBP do 12 MB.'); return;
    }
    setUploadingProductImageId(imageId);
    setProductImageError(null);
    try {
      const response = await labApi.uploadProductImageSlot(productId, imageId, file);
      const uploaded = response.data.data as LabProductImage;
      setProductImages(current => current.map(image => image.id === imageId ? uploaded : image));
      onReload();
    } catch (error) {
      setProductImageError(apiErrorMessage(error, 'Nie udało się wgrać grafiki.'));
    } finally { setUploadingProductImageId(null); }
  };

  useEffect(() => {
    setProductImages(product.productImages || []);
  }, [product.productImages]);

  useEffect(() => {
    if (activeTab?.kind !== 'reference-images') return;
    loadReferenceImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab?.kind === 'reference-images' ? productId : null]);

  useEffect(() => {
    const handleReferenceProgress = (data: {
      labProductId: string;
      pipeline?: string;
      targetId?: string;
      fieldName?: string;
      fieldIndex: number;
      totalFields: number;
      generatedCount?: number;
      status: 'generating' | 'completed' | 'error' | 'cancelled';
      content?: string;
    }) => {
      if (data.labProductId !== productId || data.pipeline !== 'REFERENCE_IMAGES') return;

      if (data.targetId) setReferenceGenerationRunId(data.targetId);
      setReferenceGenerationProgress(prev => ({
        current: Math.min(data.fieldIndex, data.totalFields || data.fieldIndex),
        total: data.totalFields || data.fieldIndex,
        generated: data.generatedCount ?? prev.generated,
        message: data.content || '',
        status: data.status,
      }));
      setReferenceImageMessage(data.content || null);

      const completedKind = data.fieldName ? REFERENCE_FIELD_KIND[data.fieldName] : undefined;
      if (completedKind || data.status !== 'generating') {
        void loadReferenceImages(true).then(() => onReloadRef.current());
      }

      if (data.status === 'generating') {
        setGeneratingReferenceImages(true);
        return;
      }

      setGeneratingReferenceImages(false);
      setStoppingReferenceImages(false);
      setReferenceGenerationRunId(null);
      if (data.status === 'error') {
        setReferenceImageError(data.content || 'Nie udało się przygotować wszystkich zdjęć referencyjnych.');
      }
    };

    const cleanupSocket = onSocketReady(socket => {
      socket.emit('subscribe:laboratorium');
      socket.off('lab:progress', handleReferenceProgress);
      socket.on('lab:progress', handleReferenceProgress);
    });
    return () => {
      cleanupSocket?.();
      getSocket()?.off('lab:progress', handleReferenceProgress);
    };
  }, [productId, loadReferenceImages]);

  useEffect(() => {
    if (!generatingReferenceImages || referenceGenerationTargetKinds.length === 0) return;
    let cancelled = false;

    const refreshGeneratedReferences = async () => {
      const images = await loadReferenceImages(true);
      if (cancelled || images.length === 0) return;
      const completedKinds = new Set(images.map(image => image.kind));
      const generated = referenceGenerationTargetKinds.filter(kind => completedKinds.has(kind)).length;
      setReferenceGenerationProgress(prev => ({
        ...prev,
        generated: Math.max(prev.generated, generated),
        current: Math.max(prev.current, generated),
      }));

      if (generated === referenceGenerationTargetKinds.length) {
        setGeneratingReferenceImages(false);
        setStoppingReferenceImages(false);
        setReferenceGenerationRunId(null);
        setReferenceGenerationProgress(prev => ({
          ...prev,
          current: referenceGenerationTargetKinds.length,
          total: referenceGenerationTargetKinds.length,
          generated,
          status: 'completed',
          message: 'Wszystkie zdjęcia referencyjne są gotowe.',
        }));
        setReferenceImageMessage('Wszystkie zdjęcia referencyjne są gotowe.');
        onReloadRef.current();
      }
    };

    void refreshGeneratedReferences();
    const interval = window.setInterval(() => void refreshGeneratedReferences(), 2500);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [generatingReferenceImages, referenceGenerationTargetKinds, loadReferenceImages]);

  useEffect(() => {
    if (!pageModalContent) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPageModalContent(null);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [pageModalContent]);

  useEffect(() => {
    if (!referenceImageToRegenerate) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setReferenceImageToRegenerate(null);
        setReferenceRegenerationInstruction('');
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [referenceImageToRegenerate]);

  useEffect(() => {
    if (!showReferenceBatchConfigurator) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !generatingReferenceImages) {
        setShowReferenceBatchConfigurator(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showReferenceBatchConfigurator, generatingReferenceImages]);

  useEffect(() => {
    if (!referenceTypographyModal) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !regeneratingReferenceImageId) {
        setReferenceTypographyModal(null);
        setReferenceRegenerationInstruction('');
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [referenceTypographyModal, regeneratingReferenceImageId]);

  useEffect(() => {
    if (!productImagePromptModal) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !regeneratingProductImageId) {
        setProductImagePromptModal(null);
        setProductImagePromptValue('');
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [productImagePromptModal, regeneratingProductImageId]);

  useEffect(() => {
    if (!productImagesGenerateModal) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !startingProductImagesGeneration) {
        setProductImagesGenerateModal(null);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [productImagesGenerateModal, startingProductImagesGeneration]);

  useEffect(() => {
    if (!productImagePreviewModal && !productImageEditModal) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (editingProductImageId) return;
      setProductImagePreviewModal(null);
      setProductImageEditModal(null);
      setProductImageEditInstruction('');
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [productImagePreviewModal, productImageEditModal, editingProductImageId]);

  useEffect(() => {
    if (!updateModalContent) return;
    const handleProgress = (data: {
      labProductId: string;
      pipeline?: string;
      fieldName?: string;
      fieldIndex: number;
      totalFields: number;
      status: string;
      content?: string;
    }) => {
      if (data.labProductId !== productId || data.pipeline !== 'BUILDER_PAGE_UPDATE') return;
      const total = data.totalFields || 1;
      setPageUpdateProgress({
        current: Math.min(data.fieldIndex + 1, total),
        total,
        message: data.content || '',
      });
      if (data.status === 'error') {
        setPageUpdateError(data.content || 'Nie udało się zaktualizować strony produktowej.');
      }
    };
    const cleanupSocket = onSocketReady(socket => {
      socket.emit('subscribe:laboratorium');
      socket.off('lab:progress', handleProgress);
      socket.on('lab:progress', handleProgress);
    });
    return () => {
      cleanupSocket?.();
      getSocket()?.off('lab:progress', handleProgress);
      unsubscribeFromLaboratorium();
    };
  }, [updateModalContent, productId]);

  // Fetch all KB section counts on mount
  useEffect(() => {
    if (product.knowledgeBases.length === 0) return;
    labApi.getKnowledgeBases(productId).then(res => {
      const kbs = res.data.data || [];
      const counts: Record<string, number> = {};
      for (const kb of kbs) {
        counts[kb.id] = countVisibleSections(kb.sections as Record<string, unknown> | undefined);
      }
      setKbSectionCounts(counts);
    }).catch(() => {});
  }, [productId, product.knowledgeBases.length]);

  /* ─── KB loading (when entering kb-detail) ─── */
  useEffect(() => {
    if (activeTab?.kind !== 'kb-detail') return;
    const kbId = activeTab.kbId;

    setLoadingKb(true);
    setKbSections({});
    setKbEditingField(null);

    labApi.getKnowledgeBases(productId).then(res => {
      const kbs = res.data.data || [];
      const found = kbs.find((kb: KnowledgeBase) => kb.id === kbId);
      if (found?.sections) {
        setKbSections(found.sections as SectionData);
        setKbSectionCounts(prev => ({ ...prev, [kbId]: countVisibleSections(found.sections as Record<string, unknown>) }));
      }
    }).catch(err => {
      console.error('Failed to load KB sections:', err);
    }).finally(() => {
      setLoadingKb(false);
    });
  }, [activeTab?.kind === 'kb-detail' ? (activeTab as any).kbId : null, productId]);

  /* ─── KB handlers ─── */
  const handleKbRegenerateField = async (fieldName: string) => {
    setKbRegenerating(fieldName);
    try {
      await labApi.regenerateKnowledgeField(productId, fieldName);
      const res = await labApi.getKnowledgeBases(productId);
      const kbs = res.data.data || [];
      const kbId = activeTab?.kind === 'kb-detail' ? activeTab.kbId : null;
      const found = kbs.find((kb: KnowledgeBase) => kb.id === kbId);
      if (found?.sections) {
        setKbSections(found.sections as SectionData);
      }
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setKbRegenerating(null);
    }
  };

  const handleKbStartEdit = (fieldName: string) => {
    const section = kbSections[fieldName];
    if (!section) return;
    setExpandedKbSections(prev => ({ ...prev, [fieldName]: true }));
    setKbEditingField(fieldName);
    setKbEditValue(section.edited || section.original || '');
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const handleKbSaveEdit = async () => {
    if (!kbEditingField) return;
    setKbSaving(true);
    try {
      await labApi.saveKnowledgeEdits(productId, { [kbEditingField]: kbEditValue });
      setKbSections(prev => ({
        ...prev,
        [kbEditingField]: { ...prev[kbEditingField], edited: kbEditValue, wasEdited: true },
      }));
      setKbEditingField(null);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setKbSaving(false);
    }
  };

  const handleKbDeleteSection = async (fieldName: string) => {
    try {
      await labApi.deleteKnowledgeSection(productId, fieldName);
      setKbSections(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  /* ─── Content handlers ─── */
  const handleStartEdit = (contentId: string, field: string, value: string) => {
    setExpandedContentSections(prev => ({ ...prev, [`${contentId}:${field}`]: true }));
    setEditingField({ contentId, field });
    setEditValue(value);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const handleSaveEdit = async () => {
    if (!editingField) return;
    setSaving(true);
    try {
      await labApi.saveContentEdits(productId, editingField.contentId, { [editingField.field]: editValue });
      setEditingField(null);
      onReload();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateField = async (contentId: string, field: string) => {
    setRegenerating(`${contentId}:${field}`);
    try {
      await labApi.regenerateContentField(productId, contentId, field);
      onReload();
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(null);
    }
  };

  const handleCopyAll = (content: LabContent) => {
    const text = buildContentText(content);
    navigator.clipboard.writeText(text);
    setCopiedId(content.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadMd = (content: LabContent) => {
    const text = buildContentText(content);
    const displayTitle = content.title || getDefaultTitle(product.name, content.type, content.version);
    const name = displayTitle.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_\-\.]/g, '');
    downloadMd(`${name}.md`, text);
  };

  const openReferenceBatchConfigurator = () => {
    if (!generatingReferenceImages) {
      setReferenceGenerationTargetKinds(missingReferenceImageKinds);
      setReferenceGenerationProgress({
        current: 0,
        total: missingReferenceImageKinds.length,
        generated: 0,
        message: '',
        status: 'idle',
      });
    }
    setReferenceStyleId(savedProductImageStyleId || productImageStyleCards[0]?.id || '');
    setReferenceStylePhoto(null);
    setShowReferenceBatchConfigurator(true);
    setReferenceImageError(null);
  };

  const handleGenerateReferenceImages = async () => {
    if (!completedKnowledgeBase) {
      setReferenceImageError('Generowanie zdjęć referencyjnych wymaga ukończonej bazy wiedzy.');
      return;
    }
    const targetKinds = missingReferenceImageKinds;
    if (targetKinds.includes('PRODUCT') && referenceScreenshotFiles.length < 1) {
      setReferenceImageError('Dodaj od 1 do 3 screenshotów produktu. Są potrzebne tylko do przygotowania brakującej referencji produktu.');
      return;
    }
    if (referenceScreenshotFiles.length > 3) {
      setReferenceImageError('Możesz dodać maksymalnie 3 screenshoty produktu.');
      return;
    }

    if (!referenceStyleId || (referenceStyleId === PHOTO_STYLE_ID && !referenceStylePhoto && !hasProductStyleReference)) {
      setReferenceImageError('Wybierz kartę stylu albo dodaj zdjęcie referencyjne stylu.');
      return;
    }
    setSavingReferenceStyle(true);
    setReferenceImageError(null);
    try {
      if (referenceStyleId === PHOTO_STYLE_ID && referenceStylePhoto) {
        const uploaded = await labApi.uploadReferenceImage(productId, { kind: 'PRODUCT_STYLE', image: referenceStylePhoto });
        const image = uploaded.data.data as LabReferenceImage;
        setReferenceImages(prev => [...prev.filter(item => item.kind !== 'PRODUCT_STYLE'), image]);
        setReferenceStylePhoto(null);
      }
      await labApi.saveProductImageStyle(productId, referenceStyleId);
      setSavedProductImageStyleId(referenceStyleId);
      setProductImageStyleId(referenceStyleId);
      onReload();
      if (targetKinds.length === 0) {
        setReferenceImageMessage('Zapisano źródło stylu kolejnych zdjęć.');
        setShowReferenceBatchConfigurator(false);
        return;
      }
    } catch (err) {
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się zapisać źródła stylu.'));
      return;
    } finally { setSavingReferenceStyle(false); }

    setGeneratingReferenceImages(true);
    setReferenceGenerationTargetKinds(targetKinds);
    setReferenceGenerationProgress({
      current: 0,
      total: targetKinds.length,
      generated: 0,
      message: `Przygotowuję ${targetKinds.length} brakujących ${targetKinds.length === 1 ? 'pozycję' : 'pozycji'}.`,
      status: 'generating',
    });
    setReferenceImageError(null);
    setReferenceImageMessage('Generowanie rozpoczęte. Gotowe pozycje będą pojawiać się na bieżąco.');
    try {
      const response = await labApi.generateReferenceImages(productId, {
        knowledgeBaseId: completedKnowledgeBase.id,
        screenshots: referenceScreenshotFiles,
        typographyStyleId: referenceTypographyStyle,
        typographyStyle: selectedTypographyStyleInstruction(referenceTypographyStyle),
      });
      const run = response.data.data;
      if (!run.runId || run.totalFields === 0) {
        setGeneratingReferenceImages(false);
        setReferenceGenerationProgress({ current: 0, total: 0, generated: 0, message: response.data.message, status: 'completed' });
        setReferenceImageMessage(response.data.message);
        await loadReferenceImages(true);
        return;
      }
      setReferenceGenerationRunId(run.runId);
      setReferenceGenerationTargetKinds(run.targetKinds as LabReferenceImageKind[]);
      setReferenceGenerationProgress(prev => ({ ...prev, total: run.totalFields }));
    } catch (err) {
      setGeneratingReferenceImages(false);
      setReferenceGenerationProgress(prev => ({ ...prev, status: 'error' }));
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się uruchomić generowania zdjęć referencyjnych.'));
    }
  };

  const handleStopReferenceImages = async () => {
    if (!referenceGenerationRunId || referenceGenerationProgress.generated < 1) return;
    setStoppingReferenceImages(true);
    setReferenceImageError(null);
    setReferenceImageMessage('Zatrzymuję generowanie. Gotowe zdjęcia pozostaną zapisane.');
    try {
      const response = await labApi.cancelReferenceImagesGeneration(productId, referenceGenerationRunId);
      if (!response.data.data?.wasActive) {
        setGeneratingReferenceImages(false);
        setReferenceGenerationRunId(null);
        setStoppingReferenceImages(false);
        await loadReferenceImages(true);
      }
    } catch (err) {
      setStoppingReferenceImages(false);
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się zatrzymać generowania zdjęć referencyjnych.'));
    }
  };

  const handleGenerateBundleReference = async (kind: LabReferenceImageKind, quantity: number, custom = false) => {
    if (!completedKnowledgeBase) {
      setReferenceImageError('Generowanie grafiki pakietu wymaga ukończonej bazy wiedzy.');
      return;
    }
    if (!referenceImagesByKind.has('PRODUCT')) {
      setReferenceImageError('Najpierw przygotuj bazowe zdjęcie referencyjne produktu.');
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) {
      setReferenceImageError('Wpisz liczbę sztuk od 1 do 99.');
      return;
    }

    setGeneratingBundleKind(kind);
    setReferenceImageError(null);
    setReferenceImageMessage(`Generuję packshot pakietu ${quantity}x...`);
    try {
      const res = await labApi.generateBundleReferenceImage(productId, {
        knowledgeBaseId: completedKnowledgeBase.id,
        quantity,
        custom,
        instruction: referenceRegenerationInstruction.trim() || undefined,
      });
      const generated = res.data.data as LabReferenceImage | undefined;
      if (generated) {
        setReferenceImages(prev => [...prev.filter(image => image.kind !== generated.kind), generated]);
      }
      await loadReferenceImages(true);
      setReferenceImageMessage(`Pakiet ${quantity}x został wygenerowany i zapisany na Dysku Google.`);
      setBundleConfigurator(null);
      onReload();
    } catch (err) {
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się wygenerować grafiki pakietu.'));
    } finally {
      setGeneratingBundleKind(null);
    }
  };

  const handleReferenceScreenshotChange = (files: FileList | null) => {
    if (generatingReferenceImages || !files?.length) return;
    const selected = Array.from(files);
    if (selected.length > 3) {
      setReferenceImageError('Wybierz maksymalnie 3 screenshoty produktu.');
      return;
    }
    if (selected.some(file => !['image/png', 'image/jpeg', 'image/webp'].includes(file.type))) {
      setReferenceImageError('Screenshoty muszą być plikami JPG, PNG albo WEBP.');
      return;
    }
    setReferenceScreenshotFiles(selected);
    if (selected.length > 0) {
      setReferenceImageError(null);
    }
  };

  const openBundleConfigurator = (kind: LabReferenceImageKind, file?: File) => {
    const image = referenceImagesByKind.get(kind);
    setCustomBundleQuantity(kind === 'BUNDLE_2' ? '2' : kind === 'BUNDLE_3' ? '3' : String(image?.metadata?.bundleQuantity || ''));
    setReferenceRegenerationInstruction('');
    setReferenceImageError(null);
    setBundleConfigurator({ kind, file });
  };

  const handleUploadSingleReferenceImage = async (kind: LabReferenceImageKind, file: File | null, quantity?: number) => {
    if (!file) {
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 12 * 1024 * 1024) {
      setReferenceImageError('Wybierz zdjęcie JPG, PNG lub WEBP do 12 MB.');
      return;
    }
    if (kind === 'BUNDLE_CUSTOM' && quantity === undefined) { openBundleConfigurator(kind, file); return; }
    setUploadingReferenceKind(kind);
    setReferenceImageError(null);
    setReferenceImageMessage(null);
    try {
      const res = await labApi.uploadReferenceImage(productId, { kind, image: file, quantity });
      const uploaded = res.data?.data as LabReferenceImage | undefined;

      if (uploaded) {
        setReferenceImages(prev => {
          const map = new Map(prev.map(image => [image.kind, image]));
          map.set(uploaded.kind, uploaded);
          return REFERENCE_IMAGE_ORDER
            .map(kind => map.get(kind))
            .filter((image): image is LabReferenceImage => Boolean(image));
        });
      }
      await loadReferenceImages(true);
      setReferenceImageMessage(`Zapisano: ${REFERENCE_IMAGE_LABELS[kind]}.`);
      setBundleConfigurator(null);
      onReload();
    } catch (err) {
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się wgrać zdjęcia referencyjnego.'));
    } finally {
      setUploadingReferenceKind(null);
    }
  };

  const openReferenceTypographyModal = (image: LabReferenceImage | null) => {
    setReferenceTypographyMode('style');
    if (image?.kind === 'BEFORE_AFTER') {
      const savedStyleId = typeof image.metadata?.staticTypographyStyleId === 'string'
        ? image.metadata.staticTypographyStyleId
        : '';
      if (TYPOGRAPHY_STYLE_OPTIONS.some(option => option.id === savedStyleId)) {
        setReferenceTypographyStyle(savedStyleId);
      }
    }
    setReferenceTypographyModal({ image });
    setReferenceRegenerationInstruction('');
    setReferenceImageError(null);
  };

  const closeReferenceTypographyModal = () => {
    setReferenceTypographyModal(null);
    setReferenceRegenerationInstruction('');
    setReferenceTypographyMode('style');
  };

  const openReferenceRegenerationModal = (image: LabReferenceImage) => {
    if (image.kind === 'BEFORE_AFTER') {
      openReferenceTypographyModal(image);
      return;
    }
    setReferenceImageToRegenerate(image);
    setReferenceRegenerationInstruction('');
    setReferenceImageError(null);
  };

  const closeReferenceRegenerationModal = () => {
    setReferenceImageToRegenerate(null);
    setReferenceRegenerationInstruction('');
  };

  const handleRegenerateReferenceImage = async () => {
    if (!referenceImageToRegenerate) return;
    if (!completedKnowledgeBase) {
      setReferenceImageError('Regeneracja zdjęcia referencyjnego wymaga ukończonej bazy wiedzy.');
      return;
    }
    if (referenceScreenshotFiles.length > 3) {
      setReferenceImageError('Możesz dodać maksymalnie 3 screenshoty produktu.');
      return;
    }

    setRegeneratingReferenceImageId(referenceImageToRegenerate.id);
    setReferenceImageError(null);
    setReferenceImageMessage(null);
    try {
      const res = await labApi.regenerateReferenceImage(productId, referenceImageToRegenerate.id, {
        knowledgeBaseId: completedKnowledgeBase.id,
        screenshots: referenceScreenshotFiles,
        instruction: referenceRegenerationInstruction,
        typographyStyleId: referenceImageToRegenerate.kind === 'BEFORE_AFTER'
          ? referenceTypographyStyle
          : undefined,
        typographyStyle: referenceImageToRegenerate.kind === 'BEFORE_AFTER'
          ? selectedTypographyStyleInstruction(referenceTypographyStyle)
          : undefined,
        typographyMode: referenceImageToRegenerate.kind === 'BEFORE_AFTER' ? 'style' : undefined,
      });
      const updated = res.data.data as LabReferenceImage | undefined;
      if (updated) {
        setReferenceImages(prev => prev.map(image => image.id === updated.id ? updated : image));
      }
      await loadReferenceImages(true);
      setReferenceImageMessage(referenceImageToRegenerate.kind === 'BEFORE_AFTER'
        ? 'Plansza typografii została podmieniona na wybrany styl.'
        : 'Zdjęcie referencyjne zostało zregenerowane.');
      closeReferenceRegenerationModal();
      onReload();
    } catch (err) {
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się zregenerować zdjęcia referencyjnego.'));
    } finally {
      setRegeneratingReferenceImageId(null);
    }
  };

  const handleSaveReferenceTypography = async () => {
    if (!referenceTypographyModal) return;
    if (!completedKnowledgeBase) {
      setReferenceImageError('Generowanie typografii wymaga ukończonej bazy wiedzy.');
      return;
    }
    if (referenceTypographyMode === 'prompt' && !referenceRegenerationInstruction.trim()) {
      setReferenceImageError('Wpisz własny prompt dla typografii albo wybierz tryb gotowego stylu.');
      return;
    }

    const existingImage = referenceTypographyModal.image;
    const busyId = existingImage?.id || NEW_REFERENCE_TYPOGRAPHY_ID;
    setRegeneratingReferenceImageId(busyId);
    setReferenceImageError(null);
    setReferenceImageMessage(null);
    try {
      const payload = {
        knowledgeBaseId: completedKnowledgeBase.id,
        instruction: referenceTypographyMode === 'prompt' ? referenceRegenerationInstruction : undefined,
        typographyStyleId: referenceTypographyMode === 'style' ? referenceTypographyStyle : undefined,
        typographyStyle: referenceTypographyMode === 'style'
          ? selectedTypographyStyleInstruction(referenceTypographyStyle)
          : undefined,
        typographyMode: referenceTypographyMode,
      };
      const res = existingImage
        ? await labApi.regenerateReferenceImage(productId, existingImage.id, {
          ...payload,
          screenshots: [],
        })
        : await labApi.generateReferenceTypography(productId, payload);
      const updated = res.data.data as LabReferenceImage | undefined;
      if (updated) {
        setReferenceImages(prev => {
          const map = new Map(prev.map(image => [image.kind, image]));
          map.set(updated.kind, updated);
          return REFERENCE_IMAGE_ORDER
            .map(kind => map.get(kind))
            .filter((image): image is LabReferenceImage => Boolean(image));
        });
      }
      await loadReferenceImages(true);
      setReferenceImageMessage(referenceTypographyMode === 'prompt'
        ? 'Plansza typografii została wygenerowana według własnego promptu.'
        : existingImage
          ? 'Plansza typografii została podmieniona na wybrany styl.'
          : 'Plansza typografii została wygenerowana.');
      closeReferenceTypographyModal();
      onReload();
    } catch (err) {
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się zapisać planszy typografii.'));
    } finally {
      setRegeneratingReferenceImageId(null);
    }
  };

  const handleDeleteReferenceImage = async (image: LabReferenceImage) => {
    const confirmed = await confirm({
      title: 'Usunąć zdjęcie referencyjne?',
      message: 'Zdjęcie zostanie usunięte również z folderu Google Drive.',
      confirmLabel: 'Usuń',
      cancelLabel: 'Anuluj',
      danger: true,
    });
    if (!confirmed) return;

    setDeletingReferenceImageId(image.id);
    setReferenceImageError(null);
    setReferenceImageMessage(null);
    try {
      await labApi.deleteReferenceImage(productId, image.id);
      setReferenceImages(prev => prev.filter(item => item.id !== image.id));
      setReferenceImageMessage('Zdjęcie referencyjne zostało usunięte z EcomFlow i Dysku Google.');
      onReload();
    } catch (err) {
      setReferenceImageError(apiErrorMessage(err, 'Nie udało się usunąć zdjęcia referencyjnego.'));
    } finally {
      setDeletingReferenceImageId(null);
    }
  };

  const openProductImagesGenerateModal = (content: LabContent) => {
    if (!hasVisualProductReference) {
      setProductImageError('Dodaj najpierw zdjęcie referencyjne produktu, opakowania albo zestawu.');
      return;
    }
    setProductImagesGenerateModal(content);
    setProductImageError(null);
    setProductImageGenerationLanguage('pl');
    setSelectedProductImageIds([]);
    setProductImageStyleId(prev => savedProductImageStyleId || prev || productImageStyleCards[0]?.id || (hasProductStyleReference ? PRODUCT_STYLE_REFERENCE_STYLE_ID : ''));
  };

  const closeProductImagesGenerateModal = () => {
    if (startingProductImagesGeneration) return;
    setProductImagesGenerateModal(null);
  };

  const handleStartProductImagesGeneration = async () => {
    if (!productImagesGenerateModal) return;
    if (!hasVisualProductReference) {
      setProductImageError('Dodaj najpierw zdjęcie referencyjne produktu, opakowania albo zestawu.');
      return;
    }
    if (!canGeneratePageImages(productImagesGenerateModal)) {
      setProductImageError('Najpierw poczekaj na ukończenie treści sprzedażowej. Grafiki korzystają z gotowych sekcji strony.');
      return;
    }
    const existingImages = productImagesByContentId.get(productImagesGenerateModal.id) || [];
    const isRegeneration = existingImages.some(image => Boolean(image.driveFileId));
    if (isRegeneration && selectedProductImageIds.length === 0) {
      setProductImageError('Wybierz co najmniej jedną grafikę do ponownego wygenerowania.');
      return;
    }
    if (!isRegeneration && selectableProductImageStyleOptions.length > 0 && !productImageStyleId) {
      setProductImageError('Wybierz kartę stylu dla grafik.');
      return;
    }

    setStartingProductImagesGeneration(true);
    setProductImageError(null);
    try {
      await onGenerateProductImages({
        labContentId: productImagesGenerateModal.id,
        knowledgeBaseId: productImagesGenerateModal.knowledgeBaseId || completedKnowledgeBase?.id || undefined,
        language: productImageGenerationLanguage,
        styleId: productImageStyleId || undefined,
        quality: productImageQuality,
        regenerateImageIds: isRegeneration ? selectedProductImageIds : undefined,
      });
      setProductImagesGenerateModal(null);
    } catch (err) {
      setProductImageError(apiErrorMessage(err, 'Nie udało się rozpocząć generowania zdjęć produktowych.'));
    } finally {
      setStartingProductImagesGeneration(false);
    }
  };

  const openProductImagePromptModal = (image: LabProductImage) => {
    setProductImagePromptModal(image);
    setProductImagePromptValue(image.prompt || '');
    setProductImageError(null);
  };

  const closeProductImagePromptModal = () => {
    if (regeneratingProductImageId) return;
    setProductImagePromptModal(null);
    setProductImagePromptValue('');
  };

  const handleRegenerateProductImage = async () => {
    if (!productImagePromptModal) return;
    const prompt = productImagePromptValue.trim();
    if (!prompt) {
      setProductImageError('Prompt nie może być pusty.');
      return;
    }

    setRegeneratingProductImageId(productImagePromptModal.id);
    setProductImageError(null);
    try {
      const res = await labApi.regenerateProductImage(productId, productImagePromptModal.id, { prompt, quality: productImageQuality });
      const updated = res.data.data as LabProductImage | undefined;
      if (updated) {
        setProductImages(prev => prev.map(image => image.id === updated.id ? updated : image));
      }
      await loadProductImages(true);
      setProductImagePromptModal(null);
      setProductImagePromptValue('');
      onReload();
    } catch (err) {
      setProductImageError(apiErrorMessage(err, 'Nie udało się zregenerować zdjęcia produktowego.'));
    } finally {
      setRegeneratingProductImageId(null);
    }
  };

  const handleSaveProductImagePrompt = async () => {
    if (!productImagePromptModal) return;
    const prompt = productImagePromptValue.trim();
    if (!prompt) {
      setProductImageError('Prompt nie może być pusty.');
      return;
    }

    setSavingProductImagePrompt(true);
    setProductImageError(null);
    try {
      const res = await labApi.saveProductImagePrompt(productId, productImagePromptModal.id, { prompt });
      const updated = res.data.data as LabProductImage | undefined;
      if (updated) {
        setProductImages(prev => prev.map(image => image.id === updated.id ? updated : image));
        setProductImagePromptModal(updated);
      }
      setProductImageError(null);
    } catch (err) {
      setProductImageError(apiErrorMessage(err, 'Nie udało się zapisać promptu zdjęcia produktowego.'));
    } finally {
      setSavingProductImagePrompt(false);
    }
  };

  const openProductImageEditModal = (image: LabProductImage) => {
    setProductImageEditReference(null);
    setProductImageEditMode('local');
    setProductImageEditModal(image);
    setProductImageEditInstruction('');
    setProductImageError(null);
  };

  const handleEditProductImage = async () => {
    if (!productImageEditModal) return;
    const instruction = productImageEditInstruction.trim();
    if (!instruction && productImageEditMode !== 'reference_rebuild') {
      setProductImageError('Wpisz komentarz do modyfikacji grafiki.');
      return;
    }

    setEditingProductImageId(productImageEditModal.id);
    setProductImageError(null);
    try {
      const res = await labApi.editProductImage(productId, productImageEditModal.id, { instruction, editMode: productImageEditMode, reference: productImageEditReference || undefined, quality: productImageQuality });
      const updated = res.data.data as LabProductImage | undefined;
      if (updated) {
        setProductImages(prev => prev.map(image => image.id === updated.id ? updated : image));
      }
      await loadProductImages(true);
      setProductImageEditModal(null);
      setProductImageEditInstruction('');
      onReload();
    } catch (err) {
      setProductImageError(apiErrorMessage(err, 'Nie udało się zmodyfikować zdjęcia produktowego.'));
    } finally {
      setEditingProductImageId(null);
      setProductImageEditReference(null);
    }
  };

  const handleDeleteProductImage = async (image: LabProductImage) => {
    const confirmed = await confirm({
      title: 'Usunąć zdjęcie produktowe?',
      message: 'Usuniesz obraz również z Dysku Google. Pusty kafelek i prompt pozostaną, aby można było wygenerować go ponownie.',
      confirmLabel: 'Usuń',
      cancelLabel: 'Anuluj',
      danger: true,
    });
    if (!confirmed) return;

    setDeletingProductImageId(image.id);
    setProductImageError(null);
    try {
      await labApi.deleteProductImage(productId, image.id);
      await loadProductImages(true);
      onReload();
    } catch (err) {
      setProductImageError(apiErrorMessage(err, 'Nie udało się usunąć zdjęcia produktowego.'));
    } finally {
      setDeletingProductImageId(null);
    }
  };

  const openGeneratePageModal = async (content: LabContent) => {
    setPageModalContent(content);
    setGeneratedPage(null);
    setPageGenerationError(null);
    setBrandStoreSelection({ integrationId: null, storeName: null, locked: false, warning: null });
    setBuilderPageConfig(null);
    setLoadingPageOptions(true);
    const displayTitle = content.title || getDefaultTitle(product.name, content.type, content.version);
    setPageForm({
      integrationId: '',
      status: 'DRAFT',
      title: displayTitle,
      slug: '',
      compressImages: true,
    });

    try {
      const [storesRes, pipelineRes, brandStoreRes] = await Promise.all([
        storeApi.getAll(),
        labApi.getPipeline(content.type),
        labApi.getBrandStore(productId),
      ]);
      const config = pipelineRes.data.data?.builderPage as BuilderPageConfig | null | undefined;
      setBuilderPageConfig(config || null);
      if (!config) {
        setPageGenerationError('Ten pipeline nie ma włączonego generowania strony w Kreatorze.');
      } else if (!hasBuilderTemplate(config)) {
        setPageGenerationError('Ten pipeline nie ma wgranego technicznego szablonu JSON w Panelu Super Admina.');
      }
      const stores = storesRes.data.filter(store => store.engineEnabled && store.engineIntegrationId);
      setEngineStores(stores);
      const brandSelection = brandStoreRes.data.data || {
        integrationId: null,
        storeName: null,
        locked: false,
        warning: null,
      };
      setBrandStoreSelection(brandSelection);
      const preferred = brandSelection.locked && brandSelection.integrationId
        ? stores.find(store => store.engineIntegrationId === brandSelection.integrationId)
        : stores.find(store => store.id === product.storeId) || stores[0];
      if (!preferred?.engineIntegrationId) {
        setPageGenerationError('Brak sklepu z aktywnym Kreatorem.');
        return;
      }

      setPageForm(prev => ({ ...prev, integrationId: preferred.engineIntegrationId || '' }));
    } catch (err) {
      console.error('Failed to load builder options:', err);
      setPageGenerationError('Nie udało się pobrać konfiguracji generowania strony.');
    } finally {
      setLoadingPageOptions(false);
    }
  };

  const handleGenerateBuilderPage = async () => {
    if (!pageModalContent) return;
    if (!pageForm.integrationId) {
      setPageGenerationError('Wybierz sklep z aktywnym Kreatorem.');
      return;
    }
    if (!hasBuilderTemplate(builderPageConfig)) {
      setPageGenerationError('Pipeline nie ma wgranego technicznego szablonu JSON w Panelu Super Admina.');
      return;
    }

    setGeneratingPage(true);
    setPageGenerationError(null);
    try {
      const res = await labApi.generateBuilderPageFromContent(productId, pageModalContent.id, {
        integrationId: pageForm.integrationId,
        status: pageForm.status,
        title: isProductBuilderConfig(builderPageConfig) ? undefined : pageForm.title || undefined,
        slug: pageForm.slug || undefined,
        compressImages: pageForm.compressImages,
      });
      setGeneratedPage(res.data.data as GeneratedBuilderPage);
      onReload();
    } catch (err: any) {
      console.error('Failed to generate builder page:', err);
      setPageGenerationError(err?.response?.data?.error || 'Nie udało się wygenerować strony w Kreatorze.');
    } finally {
      setGeneratingPage(false);
    }
  };

  const openUpdatePageModal = (content: LabContent) => {
    setUpdateModalContent(content);
    setPageUpdateError(null);
    setUpdatedPage(null);
    setPageUpdateProgress({ current: 0, total: 0, message: '' });
    setCompressPageUpdateImages(true);
  };

  const closeUpdatePageModal = () => {
    if (updatingPageScope) return;
    setUpdateModalContent(null);
    setPageUpdateError(null);
    setUpdatedPage(null);
    setPageUpdateProgress({ current: 0, total: 0, message: '' });
  };

  const handleUpdateBuilderPage = async (scope: BuilderPageUpdateScope) => {
    if (!updateModalContent) return;
    setUpdatingPageScope(scope);
    setPageUpdateError(null);
    setUpdatedPage(null);
    setPageUpdateProgress({ current: 0, total: 8, message: 'Uruchamiam aktualizację strony produktowej...' });
    try {
      const res = await labApi.updateBuilderPageFromContent(productId, updateModalContent.id, {
        scope,
        compressImages: compressPageUpdateImages,
      });
      setUpdatedPage(res.data.data as GeneratedBuilderPage);
      setPageUpdateProgress({ current: 8, total: 8, message: 'Aktualizacja strony produktowej zakończona.' });
      await loadProductImages(true);
      onReload();
    } catch (err: any) {
      console.error('Failed to update builder page:', err);
      setPageUpdateError(apiErrorMessage(err, 'Nie udało się zaktualizować strony produktowej.'));
    } finally {
      setUpdatingPageScope(null);
    }
  };

  // Title editing
  const handleStartTitleEdit = (content: LabContent) => {
    setEditingTitleId(content.id);
    setTitleValue(content.title || getDefaultTitle(product.name, content.type, content.version));
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  const handleSaveTitle = async (contentId: string) => {
    setSavingTitle(true);
    try {
      await labApi.updateContentTitle(productId, contentId, titleValue);
      setEditingTitleId(null);
      onReload();
    } catch (err) {
      console.error('Failed to save title:', err);
    } finally {
      setSavingTitle(false);
    }
  };

  // KB copy/download
  const handleCopyKb = () => {
    const text = buildKbText(kbSections, kbPipelineItems);
    navigator.clipboard.writeText(text);
    setCopiedId('kb');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadKb = () => {
    const text = buildKbText(kbSections, kbPipelineItems);
    const kbName = activeTab?.kind === 'kb-detail' ? activeTab.kb.name.replace(/\s+/g, '_') : 'baza_wiedzy';
    downloadMd(`${kbName}.md`, text);
  };

  /* ─── Active content type items ─── */
  const activeContentType = activeTab?.kind === 'content' ? activeTab.type : null;
  const activeContentTypes = activeContentType ? contentTypesForMenuType(activeContentType) : [];
  const currentContentItems = activeContentTypes
    .flatMap(type => contentsByType[type] || [])
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  const completedContentCandidates = currentContentItems
    .filter(content => content.status === 'COMPLETE')
    .map(content => ({ id: content.id, updatedAt: content.updatedAt || '' }));
  const { data: missingContentFieldsById = {} } = useQuery({
    queryKey: ['lab-content-missing-fields', productId, completedContentCandidates],
    queryFn: async (): Promise<Record<string, Array<{ name: string; title: string }>>> => {
      const results = await Promise.all(completedContentCandidates.map(async candidate => {
        const response = await labApi.getContentMissingFields(productId, candidate.id);
        return [candidate.id, response.data.data.missingFields] as const;
      }));
      return Object.fromEntries(results);
    },
    enabled: completedContentCandidates.length > 0,
    staleTime: 15_000,
  });
  const currentSection = activeContentType ? CONTENT_SECTIONS.find(s => s.type === activeContentType) : null;
  const showFunnelTabs = Boolean(activeContentType && FUNNEL_CONTENT_TYPES.has(activeContentType));
  const activeFunnelStage = activeContentType
    ? (activeFunnelStageByType[activeContentType] || 'TOFU')
    : 'TOFU';
  const contentGenerationInputs = showFunnelTabs
    ? { funnel_stage: activeFunnelStage, generation_mode: 'knowledge' }
    : undefined;
  const startNewContent = (type: string, initialInputs?: Record<string, string>) => {
    if (IMPORTABLE_PAGE_PIPELINES.has(type)) {
      setContentSourceModal({
        pipeline: type as 'ADVERTORIAL' | 'LISTICALE' | 'QUIZ' | 'LANDING',
        initialInputs,
      });
      return;
    }
    onNewContent(type, initialInputs);
  };

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="flex gap-5">
      {replaceTarget && <AssetTextReplacementModal productId={productId} target={replaceTarget} onClose={() => setReplaceTarget(null)} onSaved={() => {
        if (replaceTarget.kind === 'knowledge') {
          void labApi.getKnowledge(productId, replaceTarget.id).then(res => setKbSections(res.data.data.sections));
        }
        onReload();
      }} />}
      {/* ─── LEFT: Vertical menu ─── */}
      <div className="w-64 shrink-0 space-y-1">
        {/* KB section header */}
        <div className="mb-1">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-primary/70" />
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Baza wiedzy</span>
            </div>
            <button
              onClick={onNewKb}
              className="p-1 text-text-muted hover:text-accent-primary transition-colors"
              title="Nowa baza wiedzy"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          {product.knowledgeBases.length === 0 ? (
            <button
              onClick={onNewKb}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-dashed border-accent-primary/20 text-text-muted hover:text-accent-primary hover:border-accent-primary/40 transition-all text-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              Utwórz bazę wiedzy
            </button>
          ) : (
            <button
              onClick={() => setActiveTab({ kind: 'kb-list' })}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left group ${
                activeTab?.kind === 'kb-list' || activeTab?.kind === 'kb-detail'
                  ? 'bg-accent-primary/10 border border-accent-primary/30 text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent'
              }`}
            >
              <Brain className={`w-4 h-4 shrink-0 ${
                activeTab?.kind === 'kb-list' || activeTab?.kind === 'kb-detail' ? 'text-accent-primary' : 'text-text-disabled group-hover:text-accent-primary/60'
              }`} />
              <span className="flex-1 text-xs font-medium">Baza wiedzy</span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                activeTab?.kind === 'kb-list' || activeTab?.kind === 'kb-detail'
                  ? 'bg-accent-primary/15 text-accent-primary' : 'bg-surface-hover/50 text-text-disabled'
              }`}>
                {product.knowledgeBases.length}
              </span>
            </button>
          )}
          <button
            onClick={() => hasCompleteKb ? setActiveTab({ kind: 'reference-images' }) : undefined}
            disabled={!hasCompleteKb}
            className={`mt-1 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left group ${
              activeTab?.kind === 'reference-images'
                ? 'bg-accent-secondary/10 border border-accent-secondary/30 text-text-primary'
                : hasCompleteKb
                  ? 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent'
                  : 'text-text-disabled border border-transparent opacity-50 cursor-not-allowed'
            }`}
          >
            <ImageIcon className={`w-4 h-4 shrink-0 ${
              activeTab?.kind === 'reference-images' ? 'text-accent-secondary' : 'text-text-disabled group-hover:text-accent-secondary/70'
            }`} />
            <span className="flex-1 text-xs font-medium">Zdjęcia referencyjne</span>
            {referenceImages.length > 0 && (
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                activeTab?.kind === 'reference-images' ? 'bg-accent-secondary/15 text-accent-secondary' : 'bg-surface-hover/50 text-text-muted'
              }`}>
                {referenceImages.length}
              </span>
            )}
          </button>
          <button
            onClick={() => hasCompleteKb ? setActiveTab({ kind: 'video-bank' }) : undefined}
            disabled={!hasCompleteKb}
            className={`mt-1 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left group ${
              activeTab?.kind === 'video-bank'
                ? 'bg-accent-secondary/10 border border-accent-secondary/30 text-text-primary'
                : hasCompleteKb
                  ? 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent'
                  : 'text-text-disabled border border-transparent opacity-50 cursor-not-allowed'
            }`}
          >
            <Film className={`w-4 h-4 shrink-0 ${
              activeTab?.kind === 'video-bank' ? 'text-accent-secondary' : 'text-text-disabled group-hover:text-accent-secondary/70'
            }`} />
            <span className="flex-1 text-xs font-medium">Bank wideo</span>
          </button>
        </div>

        {/* Content type groups */}
        <div className="h-4" />

        <div className="space-y-5">
          {CONTENT_MENU_GROUPS.map((group, groupIndex) => {
            const sections = group.types
              .map(type => CONTENT_SECTIONS.find(section => section.type === type))
              .filter((section): section is (typeof CONTENT_SECTIONS)[number] => Boolean(section));

            if (sections.length === 0) return null;

            return (
              <div key={group.label} className={groupIndex === 0 ? '' : 'pt-1'}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-primary/70" />
                    <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{group.label}</span>
                  </div>
                  {groupIndex === 0 && (
                    <button
                      onClick={onOpenBatchModal}
                      disabled={!hasCompleteKb}
                      className="p-1 text-text-muted hover:text-accent-primary transition-colors disabled:opacity-30"
                      title="Batch Generate"
                    >
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="space-y-0.5">
                  {sections.map(section => {
                    const count = contentTypesForMenuType(section.type).reduce(
                      (sum, type) => sum + (contentsByType[type]?.length || 0),
                      0,
                    );
                    const Icon = section.icon;
                    const isActive = activeTab?.kind === 'content' && activeTab.type === section.type;
                    return (
                      <button
                        key={section.type}
                        onClick={() => hasCompleteKb ? setActiveTab({ kind: 'content', type: section.type }) : undefined}
                        disabled={!hasCompleteKb}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left group ${
                          isActive
                            ? `${section.bgClass} border ${section.borderClass} text-text-primary`
                            : hasCompleteKb
                              ? 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent'
                              : 'text-text-disabled border border-transparent opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? section.colorClass : 'text-text-disabled'}`} />
                        <span className="flex-1 text-xs font-medium truncate">{section.label}</span>
                        {count > 0 && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            isActive ? 'bg-surface-hover/60 ' + section.colorClass : 'bg-surface-hover/50 text-text-muted'
                          }`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── RIGHT: Content area ─── */}
      <div className="flex-1 min-w-0">
        {/* No selection placeholder */}
        {!activeTab && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Brain className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-text-muted">Wybierz element z menu po lewej</p>
            </div>
          </div>
        )}

        {/* ─── KB List — shows all knowledge bases as cards ─── */}
        {activeTab?.kind === 'kb-list' && (
          <div className="space-y-3">
            {/* Header */}
            <div className="bg-accent-primary/5 rounded-xl border border-accent-primary/20 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-primary/10 flex items-center justify-center border border-accent-primary/20">
                    <Brain className="w-5 h-5 text-accent-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">Baza wiedzy</h3>
                    <span className="text-[10px] text-text-muted">
                      {product.knowledgeBases.length} {product.knowledgeBases.length === 1 ? 'baza' : product.knowledgeBases.length < 5 ? 'bazy' : 'baz'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onNewKb}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-lg hover:bg-accent-primary/15 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Nowa baza wiedzy
                </button>
              </div>
            </div>

            {/* KB cards */}
            {product.knowledgeBases.length === 0 ? (
              <div className="bg-surface-raised rounded-xl border border-border p-8 text-center">
                <Brain className="w-8 h-8 text-accent-primary opacity-30 mx-auto mb-3" />
                <p className="text-xs text-text-muted mb-3">Brak baz wiedzy</p>
                <button
                  onClick={onNewKb}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-lg hover:bg-accent-primary/15 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Utwórz pierwszą bazę wiedzy
                </button>
              </div>
            ) : (
              product.knowledgeBases.map(kb => {
                const sectionCount = kbSectionCounts[kb.id] ?? countVisibleSections(kb.sections as Record<string, unknown> | undefined);
                return (
                  <button
                    key={kb.id}
                    onClick={() => setActiveTab({ kind: 'kb-detail', kbId: kb.id, kb })}
                    className="w-full bg-surface-raised rounded-xl border border-border hover:border-accent-primary/30 p-4 text-left transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-accent-primary/10 flex items-center justify-center border border-accent-primary/15 shrink-0">
                        <Brain className="w-4.5 h-4.5 text-accent-primary/70 group-hover:text-accent-primary transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-text-primary block truncate">{kb.name}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          {kb.isComplete ? (
                            <span className="flex items-center gap-1 text-[10px] text-success">
                              <CheckCircle className="w-3 h-3" /> Kompletna
                            </span>
                          ) : (
                            <span className="text-[10px] text-warning">{getKnowledgePipelineFailure(kb) || isLabPipelineCancelled(kb) ? 'Przerwana' : 'Nieukończona'}</span>
                          )}
                          <span className="text-[10px] text-text-disabled">&#x2022;</span>
                          <span className="text-[10px] text-text-muted">{sectionCount} sekcji</span>
                          {kb.createdAt && (
                            <>
                              <span className="text-[10px] text-text-disabled">&#x2022;</span>
                              <span className="flex items-center gap-1 text-[10px] text-text-muted">
                                <Calendar className="w-2.5 h-2.5" />
                                {formatDate(kb.createdAt)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-text-disabled group-hover:text-accent-primary transition-colors shrink-0" />
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {/* ─── KB Detail — sections displayed as code blocks ─── */}
        {activeTab?.kind === 'kb-detail' && (() => {
          const kb = activeTab.kb;
          const sectionCount = kbSectionCounts[kb.id] ?? 0;
          const kbFailure = knowledgeGenerationError || getKnowledgePipelineFailure(kb);
          const kbFields = orderedKbFields;

          return (
            <div className="space-y-3">
              {kbFailure && !kb.isComplete && <p role="alert" className="rounded-lg bg-danger/10 p-3 text-sm text-danger">{kbFailure}</p>}
              {/* Header with back button */}
              <div className="bg-accent-primary/5 rounded-xl border border-accent-primary/20 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setActiveTab({ kind: 'kb-list' })}
                      className="w-7 h-7 rounded-lg bg-accent-primary/10 flex items-center justify-center border border-accent-primary/20 hover:bg-accent-primary/15 transition-colors"
                      title="Wróć do listy"
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-accent-primary rotate-180" />
                    </button>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary">{kb.name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        {kb.isComplete ? (
                          <span className="flex items-center gap-1 text-[10px] text-success">
                            <CheckCircle className="w-3 h-3" /> Kompletna
                          </span>
                        ) : (
                          <span className="text-[10px] text-warning">{getKnowledgePipelineFailure(kb) || isLabPipelineCancelled(kb) ? 'Przerwana' : 'Nieukończona'}</span>
                        )}
                        <span className="text-[10px] text-text-disabled">&#x2022;</span>
                        <span className="text-[10px] text-text-muted">{sectionCount} sekcji</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!kb.isComplete && (
                      <Button variant="secondary" size="sm" onClick={() => void onResumeKnowledge(kb.id)} disabled={resumingKnowledge}
                        className="mr-2">
                        <RefreshCw className={`h-4 w-4 ${resumingKnowledge ? 'animate-spin' : ''}`} />
                        {resumingKnowledge ? 'Wznawiam...' : 'Wznów generowanie'}
                      </Button>
                    )}
                    <button type="button" onClick={() => setReplaceTarget({ id: kb.id, kind: 'knowledge' })} className="p-1.5 text-text-muted hover:text-accent-primary" title="Zamień frazę w bazie wiedzy" aria-label="Zamień frazę w bazie wiedzy"><Replace className="w-3.5 h-3.5" /></button>
                    <button
                      onClick={handleCopyKb}
                      className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                      title="Kopiuj całą bazę wiedzy"
                    >
                      {copiedId === 'kb' ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={handleDownloadKb}
                      className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                      title="Pobierz jako .md"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDeleteKb(kb.id)}
                      className="p-1.5 text-text-muted hover:text-danger transition-colors"
                      title="Usuń bazę wiedzy"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Loading */}
              {loadingKb && (
                <div className="bg-surface-raised rounded-xl border border-border flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
                </div>
              )}

              {/* Empty state */}
              {!loadingKb && kbFields.length === 0 && (
                <div className="bg-surface-raised rounded-xl border border-border p-8 text-center">
                  <Brain className="w-8 h-8 text-accent-primary opacity-30 mx-auto mb-3" />
                  <p className="text-xs text-text-muted">Baza wiedzy jest pusta - brak sekcji.</p>
                </div>
              )}

              {/* KB sections as grouped code blocks */}
              {!loadingKb && groupedKbSections.map(({ group, fields }) => (
                <div key={group} className="space-y-2">
                  <div className="flex items-center justify-between px-1 pt-2">
                    <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider">{group}</h4>
                    <span className="text-[10px] text-text-muted">{fields.length} sekcji</span>
                  </div>

                  {fields.map(fieldName => {
                    const section = kbSections[fieldName];
                    if (!section) return null;
                    const meta = kbMetaByName.get(fieldName);
                    const isEditing = kbEditingField === fieldName;
                    const isRegen = kbRegenerating === fieldName;
                    const title = getKbSectionTitle(fieldName, section, meta);
                    const rawDisplayText = section.edited || section.original || '';
                    const displayText = stripKbSectionHeading(rawDisplayText, fieldName, section, meta, title);
                    const icon = section.icon || meta?.icon;
                    const isSectionExpanded = isEditing || Boolean(expandedKbSections[fieldName]);

                    return (
                      <div key={fieldName} className="bg-surface-raised rounded-xl border border-border overflow-hidden">
                        {/* Section title bar */}
                        <div className={`flex items-center justify-between px-4 py-2.5 ${isSectionExpanded ? 'border-b border-border-subtle' : ''}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <button
                              type="button"
                              onClick={() => setExpandedKbSections(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))}
                              className="flex items-center gap-2 min-w-0 text-left hover:opacity-85 transition-opacity"
                            >
                              {icon && <span className="text-xs shrink-0">{icon}</span>}
                              <span className="text-xs font-medium text-accent-primary truncate">{title}</span>
                            </button>
                            {section.aiShortcode && (
                              <button
                                type="button"
                                onClick={() => copyShortcodeToClipboard(section.aiShortcode!, setCopiedId)}
                                className="inline-flex items-center gap-1 text-[9px] text-accent-primary px-1.5 py-0.5 bg-accent-primary/10 rounded shrink-0 hover:bg-accent-primary/15 transition-colors cursor-pointer"
                                title="Kopiuj shortcode"
                              >
                                <span>[{section.aiShortcode}]</span>
                                {copiedId === `shortcode:${section.aiShortcode}` ? <Check className="w-2.5 h-2.5 text-success" /> : <Copy className="w-2.5 h-2.5" />}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleKbStartEdit(fieldName)}
                              className="p-1 text-text-muted hover:text-accent-primary transition-colors"
                              title="Edytuj"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleKbRegenerateField(fieldName)}
                              disabled={isRegen}
                              className="p-1 text-text-muted hover:text-accent-primary transition-colors"
                              title="Regeneruj"
                            >
                              {isRegen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => handleKbDeleteSection(fieldName)}
                              className="p-1 text-text-muted hover:text-danger transition-colors"
                              title="Usuń sekcję"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => setExpandedKbSections(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))}
                              className="p-1 text-text-muted hover:text-accent-primary transition-colors"
                              title={isSectionExpanded ? 'Zwiń' : 'Rozwiń'}
                            >
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isSectionExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        </div>

                        {/* Code block content or editor */}
                        {isSectionExpanded && (isEditing ? (
                          <div className="p-4">
                            <div className="relative">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(kbEditValue);
                                  setCopiedId(fieldName);
                                  setTimeout(() => setCopiedId(null), 2000);
                                }}
                                className="absolute right-6 top-2 z-10 p-1.5 text-text-muted hover:text-accent-primary bg-surface-base/80 border border-border-subtle rounded-md transition-colors"
                                title="Kopiuj tekst"
                              >
                                {copiedId === fieldName ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                              </button>
                              <textarea
                                ref={textareaRef}
                                value={kbEditValue}
                                onChange={e => setKbEditValue(e.target.value)}
                                className="w-full min-h-[200px] bg-surface-hover/80 border border-accent-primary/30 rounded-lg p-3 pr-14 text-xs text-text-primary focus:outline-none focus:border-accent-primary/50 resize-y font-mono"
                              />
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={handleKbSaveEdit}
                                disabled={kbSaving}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-text-primary bg-accent-primary/20 border border-accent-primary/30 rounded-lg hover:bg-accent-primary/30 transition-colors"
                              >
                                {kbSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Zapisz
                              </button>
                              <button
                                onClick={() => setKbEditingField(null)}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                              >
                                <X className="w-3 h-3" />
                                Anuluj
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4">
                            <div className="relative">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(displayText);
                                  setCopiedId(fieldName);
                                  setTimeout(() => setCopiedId(null), 2000);
                                }}
                                className="absolute right-6 top-2 z-10 p-1.5 text-text-muted hover:text-accent-primary bg-surface-base/80 border border-border-subtle rounded-md transition-colors"
                                title="Kopiuj tekst"
                              >
                                {copiedId === fieldName ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                              </button>
                              <pre className="bg-surface-base/60 rounded-lg p-4 pr-14 text-xs text-text-secondary whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto font-mono">
                                {displayText}
                              </pre>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })()}

        {/* ─── Bank wideo (powtarzalne ujęcia produktu) ─── */}
        {activeTab?.kind === 'video-bank' && (
          <VideoBankPanel productId={productId} />
        )}

        {/* ─── Reference Images panel ─── */}
        {activeTab?.kind === 'reference-images' && (
          <div className="space-y-3">
            <div className="bg-accent-secondary/5 rounded-xl border border-accent-secondary/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-secondary/10 flex items-center justify-center border border-accent-secondary/20">
                    <ImageIcon className="w-5 h-5 text-accent-secondary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">Zdjęcia referencyjne</h3>
                    <span className="text-[10px] text-text-muted">
                      {requiredReferenceImageCount} z {savedProductImageStyleId === PHOTO_STYLE_ID ? 3 : REQUIRED_REFERENCE_IMAGE_ORDER.length} bazowych
                      {hasProductStyleReference ? ' + styl produktowy' : ''}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={openReferenceBatchConfigurator}
                  disabled={!hasCompleteKb || savingReferenceStyle}
                  leftIcon={generatingReferenceImages ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                >
                  {generatingReferenceImages
                    ? 'Pokaż postęp'
                    : missingReferenceImageKinds.length > 0
                      ? `Generuj brakujące (${missingReferenceImageKinds.length})`
                      : 'Konfiguruj styl'}
                </Button>
              </div>
            </div>

            {generatingReferenceImages && referenceGenerationProgress.total > 0 && (
              <div className="rounded-xl border border-accent-primary/25 bg-accent-primary/[0.06] p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Generowanie zdjęć referencyjnych</p>
                    <p className="mt-1 text-xs text-text-muted">
                      {referenceGenerationProgress.message || 'Gotowe pozycje pojawią się tutaj na bieżąco.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-accent-primary">
                      {referenceGenerationProgress.current}/{referenceGenerationProgress.total}
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleStopReferenceImages}
                      loading={stoppingReferenceImages}
                      disabled={referenceGenerationProgress.generated < 1 || !referenceGenerationRunId}
                      leftIcon={<Square />}
                    >
                      Przerwij
                    </Button>
                  </div>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-hover">
                  <div
                    className="h-full rounded-full bg-accent-primary transition-all duration-500"
                    style={{ width: `${Math.round((referenceGenerationProgress.current / referenceGenerationProgress.total) * 100)}%` }}
                  />
                </div>
                {referenceGenerationProgress.generated < 1 && (
                  <p className="text-[10px] text-text-muted">Przerwanie będzie dostępne po zapisaniu pierwszej grafiki.</p>
                )}
              </div>
            )}

            {referenceImageMessage && (
              <Alert variant="info" title="Zdjęcia referencyjne" onClose={() => setReferenceImageMessage(null)}>
                {referenceImageMessage}
              </Alert>
            )}
            {referenceImageError && (
              <Alert variant="danger" title="Nie udało się wykonać operacji" onClose={() => setReferenceImageError(null)}>
                {referenceImageError}
              </Alert>
            )}

            {!hasCompleteKb ? (
              <div className="bg-surface-raised rounded-xl border border-border">
                <EmptyState
                  icon={Lock}
                  title="Baza wiedzy wymagana"
                  description="Najpierw ukończ bazę wiedzy produktu, a potem wygeneruj referencje."
                  size="sm"
                />
              </div>
            ) : loadingReferenceImages ? (
              <div className="bg-surface-raised rounded-xl border border-border flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-accent-secondary" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {REFERENCE_IMAGE_ORDER.filter(kind => !(savedProductImageStyleId === PHOTO_STYLE_ID && kind === 'BEFORE_AFTER')).map(kind => {
                  const image = referenceImagesByKind.get(kind);
                  const imageUrl = image
                    ? labApi.getReferenceImageFileUrl(productId, image.id, image.updatedAt || image.createdAt)
                    : null;
                  const isCreatingTypography = kind === 'BEFORE_AFTER' && !image && regeneratingReferenceImageId === NEW_REFERENCE_TYPOGRAPHY_ID;
                  const isRegenerating = image?.id === regeneratingReferenceImageId || isCreatingTypography || generatingBundleKind === kind;
                  const isDeleting = image?.id === deletingReferenceImageId;
                  const isUploading = uploadingReferenceKind === kind;
                  const isProductStyleKind = kind === 'PRODUCT_STYLE';
                  const referenceBusy = generatingReferenceImages || Boolean(generatingBundleKind) || Boolean(regeneratingReferenceImageId) || Boolean(deletingReferenceImageId) || uploadingReferenceKind !== null;
                  const canRegenerate = BUNDLE_REFERENCE_IMAGE_KINDS.has(kind) ? !referenceBusy : kind === 'BEFORE_AFTER'
                    ? !isProductStyleKind && !referenceBusy
                    : Boolean(image && !isProductStyleKind && !referenceBusy);
                  const canUpload = !referenceBusy;
                  const isQueuedForGeneration = generatingReferenceImages
                    && !image
                    && referenceGenerationTargetKinds.includes(kind);

                  return (
                    <div key={kind} className="h-full bg-surface-raised rounded-xl border border-border overflow-hidden flex flex-col">
                      <div className="aspect-square bg-surface-base/60 border-b border-border-subtle flex items-center justify-center">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={REFERENCE_IMAGE_LABELS[kind]}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center px-6">
                            <ImageIcon className="w-10 h-10 text-text-disabled mx-auto mb-3" />
                            <p className="text-xs text-text-muted">
                              {isProductStyleKind ? (savedProductImageStyleId === PHOTO_STYLE_ID ? 'Wgraj wybrane źródło stylu' : 'Upload do wyboru w konfiguracji stylu') : 'Oczekuje na wygenerowanie'}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="p-3 space-y-3 flex flex-1 flex-col">
                        <div className="min-h-[78px]">
                          <h4 className="text-sm font-semibold text-text-primary">{REFERENCE_IMAGE_LABELS[kind]}</h4>
                          <p className="text-xs text-text-muted mt-1 line-clamp-2 leading-relaxed">{REFERENCE_IMAGE_DESCRIPTIONS[kind]}</p>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full ${
                            image ? 'bg-success/10 text-success' : 'bg-surface-hover text-text-muted'
                          }`}>
                            {image ? <CheckCircle className="w-3 h-3" /> : isQueuedForGeneration ? <Loader2 className="w-3 h-3 animate-spin" /> : <Loader2 className="w-3 h-3" />}
                            {image ? 'Gotowe' : isQueuedForGeneration ? 'W kolejce' : isProductStyleKind ? 'Opcjonalne' : 'Brak'}
                          </span>
                        </div>
                        {image?.aiShortcode && (
                          <button
                            type="button"
                            onClick={() => copyShortcodeToClipboard(image.aiShortcode!, setCopiedId)}
                            className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] text-accent-primary px-2 py-1.5 bg-accent-primary/10 rounded-lg hover:bg-accent-primary/15 transition-colors"
                            title="Kopiuj shortcode"
                          >
                            <span className="truncate">[{image.aiShortcode}]</span>
                            {copiedId === `shortcode:${image.aiShortcode}` ? <Check className="w-3 h-3 text-success shrink-0" /> : <Copy className="w-3 h-3 shrink-0" />}
                          </button>
                        )}
                        <div className="grid grid-cols-4 gap-1.5 mt-auto">
                          <button
                            type="button"
                            onClick={() => {
                              if (!canRegenerate) return;
                              if (BUNDLE_REFERENCE_IMAGE_KINDS.has(kind)) { openBundleConfigurator(kind); return; }
                              if (kind === 'BEFORE_AFTER') {
                                openReferenceTypographyModal(image || null);
                                return;
                              }
                              if (image) openReferenceRegenerationModal(image);
                            }}
                            disabled={!canRegenerate || referenceBusy}
                            className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={BUNDLE_REFERENCE_IMAGE_KINDS.has(kind) ? `Generuj ${REFERENCE_IMAGE_LABELS[kind]} ze wskazówką` : `Regeneruj ${REFERENCE_IMAGE_LABELS[kind]}`}
                            title={BUNDLE_REFERENCE_IMAGE_KINDS.has(kind) ? 'Generuj lub regeneruj ze wskazówką' : isProductStyleKind ? 'Podmień przez upload' : kind === 'BEFORE_AFTER' ? (image ? 'Zmień styl typografii' : 'Generuj styl typografii') : 'Regeneruj'}
                          >
                            {isRegenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          </button>
                          <label
                            className={`inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors ${
                              canUpload ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed pointer-events-none'
                            }`}
                            title={image ? 'Podmień plik' : 'Wgraj plik'}
                          >
                            {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              aria-label={`Wgraj ${REFERENCE_IMAGE_LABELS[kind]}`}
                              className="sr-only"
                              disabled={!canUpload}
                              onChange={event => {
                                const file = event.target.files?.[0] || null;
                                event.currentTarget.value = '';
                                void handleUploadSingleReferenceImage(kind, file);
                              }}
                            />
                          </label>
                          {image?.driveFileUrl ? (
                            <a
                              href={image.driveFileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors"
                              title="Otwórz na Dysku Google" aria-label={`Otwórz ${REFERENCE_IMAGE_LABELS[kind]} na Dysku Google`}
                            >
                              <HardDrive className="w-3.5 h-3.5" />
                            </a>
                          ) : (
                            <span className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/20 p-2 text-text-disabled">
                              <HardDrive className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => image && handleDeleteReferenceImage(image)}
                            disabled={!image || referenceBusy}
                            className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-danger hover:border-danger/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Usuń" aria-label={`Usuń ${REFERENCE_IMAGE_LABELS[kind]}`}
                          >
                            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        )}

        {/* ─── Content type panel ─── */}
        {activeTab?.kind === 'content' && currentSection && (
          <div className="space-y-3">
            {/* Content type header */}
            <div className={`${currentSection.bgClass} rounded-xl border ${currentSection.borderClass} p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg ${currentSection.bgClass} flex items-center justify-center border ${currentSection.borderClass}`}>
                    <currentSection.icon className={`w-5 h-5 ${currentSection.colorClass}`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">{currentSection.label}</h3>
                    <span className="text-[10px] text-text-muted">
                      {currentContentItems.length} {currentContentItems.length === 1 ? 'wariant' : currentContentItems.length < 5 ? 'warianty' : 'wariantów'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canGenerateContentType(activeContentType!) && (
                    <button
                      onClick={() => startNewContent(activeContentType!, contentGenerationInputs)}
                      disabled={generatingContent === activeContentType}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${currentSection.colorClass} ${currentSection.bgClass} border ${currentSection.borderClass} rounded-lg hover:brightness-125 transition-all disabled:opacity-50`}
                    >
                      {generatingContent === activeContentType ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Generuj
                    </button>
                  )}
                </div>
              </div>
              {showFunnelTabs && activeContentType && (
                <div className="mt-4 flex items-center gap-1 rounded-lg border border-border-subtle bg-surface-raised/50 p-1 w-fit">
                  {FUNNEL_STAGES.map(stage => {
                    const isSelected = activeFunnelStage === stage.value;
                    return (
                      <button
                        key={stage.value}
                        type="button"
                        onClick={() => setActiveFunnelStageByType(prev => ({
                          ...prev,
                          [activeContentType]: stage.value,
                        }))}
                        className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                          isSelected
                            ? `${currentSection.colorClass} ${currentSection.bgClass} border ${currentSection.borderClass}`
                            : 'text-text-muted hover:text-text-primary hover:bg-surface-hover/60 border border-transparent'
                        }`}
                      >
                        {stage.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Social Content Machine: harmonogram miesięczny nad listą treści */}
            {activeContentType === 'SOCIAL_POST' && (
              <SocialPlannerPanel productId={productId} />
            )}

            {/* Content items */}
            {currentContentItems.length === 0 ? (
              <div className="bg-surface-raised rounded-xl border border-border p-8 text-center">
                <currentSection.icon className={`w-8 h-8 ${currentSection.colorClass} opacity-30 mx-auto mb-3`} />
                <p className="text-xs text-text-muted mb-3">
                  Brak treści typu {currentSection.label}
                </p>
                {canGenerateContentType(activeContentType!) ? (
                  <button
                    onClick={() => startNewContent(activeContentType!, contentGenerationInputs)}
                    disabled={generatingContent === activeContentType}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium ${currentSection.colorClass} ${currentSection.bgClass} border ${currentSection.borderClass} rounded-lg hover:brightness-125 transition-all disabled:opacity-50`}
                  >
                    {generatingContent === activeContentType ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Generuj pierwszy wariant
                  </button>
                ) : (
                  <p className="text-xs text-text-muted max-w-md mx-auto">
                    Sekcja jest przygotowana pod statyczne kreacje graficzne. Pipeline generowania zostanie podpięty osobno po konfiguracji backendu.
                  </p>
                )}
              </div>
            ) : (
              currentContentItems.map(content => {
                const badge = getContentStatusBadge(content.status);
                const sections = (content.sections || {}) as SectionData;
                const sectionEntries = getDisplayContentSectionEntries(sections);
                const imagesForContent = productImagesByContentId.get(content.id) || [];
                const isExpanded = expandedContent === content.id;
                const isCopied = copiedId === content.id;
                const displayTitle = content.title || getDefaultTitle(product.name, content.type, content.version);
                const isEditingTitle = editingTitleId === content.id;
                const pipelineFailure = getStaleContentPipelineFailure(content);
                const missingFields = missingContentFieldsById[content.id] || [];

                return (
                  // Rozwinięta gałąź = jeden wspólny box (nagłówek + sekcje + zasoby produkcyjne)
                  <div
                    key={content.id}
                    className={isExpanded
                      ? `rounded-2xl border ${currentSection!.borderClass} bg-surface-raised/40 p-3 space-y-3 transition-all`
                      : 'space-y-3'}
                  >
                    {/* Content item header card */}
                    <div className={`group/card bg-surface-raised rounded-xl border overflow-hidden transition-all ${
                      isExpanded ? 'border-border-subtle' : 'border-border hover:border-border-strong'
                    }`}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <button
                            onClick={() => void toggleContent(content)}
                            className="shrink-0 hover:opacity-80 transition-opacity"
                          >
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                              isExpanded ? currentSection!.bgClass : 'bg-surface-hover/50'
                            }`}>
                              {isExpanded
                                ? <ChevronDown className={`w-3.5 h-3.5 ${currentSection!.colorClass}`} />
                                : <ChevronRight className="w-3.5 h-3.5 text-text-muted" />}
                            </div>
                          </button>
                          {isEditingTitle ? (
                            <div className="flex items-center gap-1.5">
                              <input
                                ref={titleInputRef}
                                value={titleValue}
                                onChange={e => setTitleValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveTitle(content.id);
                                  if (e.key === 'Escape') setEditingTitleId(null);
                                }}
                                size={Math.max(titleValue.length, 10)}
                                className="bg-surface-hover/80 border border-accent-primary/30 rounded px-2 py-1 text-sm text-text-primary focus:outline-none focus:border-accent-primary/50 min-w-[120px] max-w-full"
                              />
                              <button
                                onClick={() => handleSaveTitle(content.id)}
                                disabled={savingTitle}
                                className="p-1 text-success hover:text-success/80 transition-colors shrink-0"
                              >
                                {savingTitle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => setEditingTitleId(null)}
                                className="p-1 text-text-muted hover:text-text-primary transition-colors shrink-0"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => void toggleContent(content)}
                              className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-text-primary truncate">{displayTitle}</span>
                                <span
                                  onClick={e => { e.stopPropagation(); handleStartTitleEdit(content); }}
                                  className="p-0.5 text-text-disabled hover:text-accent-primary transition-colors shrink-0 opacity-0 group-hover/card:opacity-100 cursor-pointer"
                                  title="Zmień nazwę"
                                >
                                  <Pencil className="w-3 h-3" />
                                </span>
                              </div>
                              {content.createdAt && (
                                <span className="text-[10px] text-text-muted flex items-center gap-1 mt-0.5">
                                  <Calendar className="w-2.5 h-2.5" />
                                  {formatDate(content.createdAt)}
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                        {content.status === 'COMPLETE' && missingFields.length > 0 && (
                          <button
                            type="button"
                            onClick={() => onResumeContent(content)}
                            disabled={generatingContent === content.type}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-2.5 py-1.5 text-[11px] font-medium text-accent-primary transition-colors hover:bg-accent-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
                            title={`Uzupełnij brakujące pola: ${missingFields.map(field => field.title).join(', ')}`}
                          >
                            {generatingContent === content.type
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Plus className="w-3.5 h-3.5" />}
                            Uzupełnij
                          </button>
                        )}
                        {pipelineFailure && content.status !== 'COMPLETE' && (
                          <button
                            type="button"
                            onClick={() => onResumeContent(content)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-2.5 py-1.5 text-[11px] font-medium text-accent-primary transition-colors hover:bg-accent-primary/15"
                            title="Wznów od pierwszego nieukończonego kroku"
                          >
                            <Play className="w-3.5 h-3.5" />
                            Wznów
                          </button>
                        )}
                        <div className="flex items-center gap-0.5 shrink-0">
                          {['LANDING', 'ADVERTORIAL', 'LISTICALE', 'QUIZ'].includes(content.type) && <button type="button" onClick={() => setReplaceTarget({ id: content.id, kind: 'content' })} className="p-1.5 text-text-muted hover:text-accent-primary" title="Zamień frazę w tej stronie" aria-label="Zamień frazę w tej stronie"><Replace className="w-3.5 h-3.5" /></button>}
                          <button
                            onClick={() => handleCopyAll(content)}
                            className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                            title="Kopiuj całość jako tekst"
                          >
                            {isCopied ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => handleDownloadMd(content)}
                            className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                            title="Pobierz jako .md"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          {VIDEO_AD_CONTENT_TYPES.has(content.type) && content.status === 'COMPLETE' && (
                            <>
                              <button
                                onClick={() => setIterateVariantContent(content)}
                                className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                                title="Iteruj wariant"
                              >
                                <GitBranch className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setCampaignContent(content)}
                                className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                                title="Kampania kreacji"
                              >
                                <Rocket className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleSendToStudio(content)}
                                disabled={sendingToStudioId === content.id}
                                className="p-1.5 text-text-muted hover:text-accent-primary transition-colors disabled:opacity-40"
                                title="Wyślij do Studio"
                              >
                                {sendingToStudioId === content.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clapperboard className="w-3.5 h-3.5" />}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => openGeneratePageModal(content)}
                            className="p-1.5 text-text-muted hover:text-accent-primary transition-colors"
                            title="Generuj stronę w Kreatorze"
                          >
                            <FilePlus2 className="w-3.5 h-3.5" />
                          </button>
                          {content.type === 'LANDING' && (
                            <button
                              onClick={() => openUpdatePageModal(content)}
                              className="p-1.5 text-text-muted hover:text-accent-secondary transition-colors"
                              title="Aktualizuj stronę produktową"
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {/* Old "edit in full view" link removed — editing is inline */}
                          <button
                            onClick={() => onDeleteContent(content.id)}
                            className="p-1.5 text-text-muted hover:text-danger transition-colors"
                            title="Usuń" aria-label="Usuń"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {isExpanded && pipelineFailure && (
                      <Alert variant="danger" title="Generowanie zostało przerwane">
                        {pipelineFailure} Ukończone pola są zachowane. Wybierz „Wznów”, aby kontynuować od pierwszego brakującego kroku.
                      </Alert>
                    )}

                    {isExpanded && content.type === 'LANDING' && (
                      <ContentResourceSection enabled id={`${content.id}-images`}
                        title={`Grafiki (${imagesForContent.filter(image => image.driveFileId).length}/${imagesForContent.length})`}
                        icon={<ImageIcon />}>
                      <div className="space-y-3">
                        <input ref={productImageUploadInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                          aria-label="Wgraj grafikę produktową"
                          onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; void uploadProductImage(file); }} />
                        {getProductImagesRunFailure(content) && <Alert variant="danger" className="text-xs">{getProductImagesRunFailure(content)}</Alert>}
                        <div className="flex items-center justify-between px-1">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-4 h-4 text-accent-secondary" />
                            <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider">Zdjęcia produktowe</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openProductImagesGenerateModal(content)}
                              disabled={!canGeneratePageImages(content) || !hasVisualProductReference}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-accent-secondary/25 bg-accent-secondary/10 px-2 py-1 text-[10px] text-accent-secondary hover:bg-accent-secondary/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={!canGeneratePageImages(content)
                                ? 'Najpierw ukończ generowanie treści sprzedażowej'
                                : !hasVisualProductReference
                                  ? 'Najpierw dodaj zdjęcie referencyjne produktu, opakowania albo zestawu'
                                  : 'Generuj grafiki dla tej treści'}
                            >
                              <ImageIcon className="w-3 h-3" />
                              {imagesForContent.some(image => image.driveFileId) ? 'Generuj ponownie' : 'Generuj grafiki'}
                            </button>
                            <span className="text-[10px] text-text-muted">{imagesForContent.filter(image => image.driveFileId).length}/{imagesForContent.length} grafik</span>
                          </div>
                        </div>

                        {productImageError && (
                          <Alert variant="danger" className="text-xs">
                            {productImageError}
                          </Alert>
                        )}

                        {!hasVisualProductReference && imagesForContent.length === 0 && (
                          <Alert variant="warning" title="Najpierw dodaj zdjęcie referencyjne">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <span>
                                Generator potrzebuje co najmniej jednego zdjęcia produktu, opakowania albo zestawu.
                              </span>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setActiveTab({ kind: 'reference-images' })}
                              >
                                Przejdź do zdjęć referencyjnych
                              </Button>
                            </div>
                          </Alert>
                        )}

                        {loadingProductImages || preparingImageSlotsId === content.id ? (
                          <div className="bg-surface-raised rounded-xl border border-border flex items-center justify-center py-10">
                            <Loader2 className="w-5 h-5 animate-spin text-accent-secondary" />
                          </div>
                        ) : imagesForContent.length === 0 ? (
                          <div className="bg-surface-raised rounded-xl border border-border p-6 text-center">
                            <ImageIcon className="w-8 h-8 text-text-disabled mx-auto mb-2" />
                            <p className="text-xs text-text-muted">Brak zdjęć produktowych dla tej treści.</p>
                            {canGeneratePageImages(content) && hasVisualProductReference ? (
                              <button
                                type="button"
                                onClick={() => openProductImagesGenerateModal(content)}
                                className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-accent-secondary/25 bg-accent-secondary/10 px-3 py-2 text-xs text-accent-secondary hover:bg-accent-secondary/15 transition-colors"
                              >
                                <ImageIcon className="w-3.5 h-3.5" />
                                Generuj grafiki
                              </button>
                            ) : !canGeneratePageImages(content) ? (
                              <p className="text-[10px] text-text-disabled mt-2">
                                Generowanie grafiki odblokuje się po ukończeniu treści sprzedażowej.
                              </p>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setActiveTab({ kind: 'reference-images' })}
                                className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-warning/25 bg-warning/10 px-3 py-2 text-xs text-warning hover:bg-warning/15 transition-colors"
                              >
                                <ImageIcon className="w-3.5 h-3.5" />
                                Dodaj zdjęcie referencyjne
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {imagesForContent.map(image => {
                              const imageUrl = labApi.getProductImageFileUrl(productId, image.id, image.updatedAt || image.createdAt);
                              const isRegenerating = image.id === regeneratingProductImageId;
                              const isDeleting = image.id === deletingProductImageId;

                              return (
                                <div key={image.id} className="bg-surface-raised rounded-xl border border-border overflow-hidden">
                                  <div className="aspect-square bg-surface-base/60 border-b border-border-subtle flex items-center justify-center">
                                    {image.driveFileId ? <img
                                      src={imageUrl}
                                      alt={image.title}
                                      className="w-full h-full object-cover"
                                    /> : <EmptyState icon={ImageIcon} title="Brak grafiki" size="sm" description="Dodaj własny plik z komputera." />}
                                  </div>
                                  <div className="p-3 space-y-3">
                                    <Button size="sm" variant="secondary" className="w-full"
                                      leftIcon={<Upload className="w-3.5 h-3.5" />}
                                      loading={uploadingProductImageId === image.id}
                                      disabled={isRegenerating || isDeleting || Boolean(uploadingProductImageId)}
                                      onClick={() => chooseProductImageUpload(image.id)}>
                                      {image.driveFileId ? 'Podmień grafikę' : 'Wgraj grafikę'}
                                    </Button>
                                    <div>
                                      <h4 className="text-sm font-semibold text-text-primary line-clamp-1">{image.title}</h4>
                                      <p className="text-xs text-text-muted mt-1 line-clamp-1">{image.language?.toUpperCase() || 'PL'}</p>
                                    </div>
                                    {image.aiShortcode ? (
                                      <button
                                        type="button"
                                        onClick={() => copyShortcodeToClipboard(image.aiShortcode!, setCopiedId)}
                                        className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] text-accent-primary px-2 py-1.5 bg-accent-primary/10 rounded-lg hover:bg-accent-primary/15 transition-colors"
                                        title="Kopiuj shortcode"
                                      >
                                        <span className="truncate">[{image.aiShortcode}]</span>
                                        {copiedId === `shortcode:${image.aiShortcode}` ? <Check className="w-3 h-3 text-success shrink-0" /> : <Copy className="w-3 h-3 shrink-0" />}
                                      </button>
                                    ) : image.driveFileId ? (
                                      <div className="rounded-lg border border-warning/20 bg-warning/10 px-2 py-1.5 text-[10px] text-warning text-center">
                                        Shortcode pojawi się po kompresji i uploadzie do WP Media.
                                      </div>
                                    ) : null}
                                    <div className="grid grid-cols-5 gap-1.5">
                                      <button
                                        type="button"
                                        onClick={() => setProductImagePreviewModal(image)}
                                        disabled={isRegenerating || isDeleting || !image.driveFileId}
                                        className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Powiększ"
                                      >
                                        <Search className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openProductImagePromptModal(image)}
                                        disabled={isRegenerating || isDeleting}
                                        className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Prompt i regeneracja"
                                      >
                                        {isRegenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Info className="w-3.5 h-3.5" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openProductImageEditModal(image)}
                                        disabled={isRegenerating || isDeleting || !image.driveFileId || editingProductImageId === image.id}
                                        className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Edytuj obraz instrukcją"
                                      >
                                        {editingProductImageId === image.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                      </button>
                                      {image.driveFileUrl ? (
                                        <a
                                          href={image.driveFileUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-accent-primary hover:border-accent-primary/30 transition-colors"
                                          title="Otwórz na Dysku Google" aria-label="Otwórz na Dysku Google"
                                        >
                                          <HardDrive className="w-3.5 h-3.5" />
                                        </a>
                                      ) : (
                                        <span className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/20 p-2 text-text-disabled">
                                          <HardDrive className="w-3.5 h-3.5" />
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteProductImage(image)}
                                        disabled={isRegenerating || isDeleting}
                                        className="inline-flex items-center justify-center rounded-lg border border-border-subtle bg-surface-hover/40 p-2 text-text-muted hover:text-danger hover:border-danger/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Usuń"
                                      >
                                        {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                      </div>
                      </ContentResourceSection>
                    )}

                    {isExpanded && <ContentResourceSection enabled={content.type === 'LANDING'} id={`${content.id}-texts`}
                      title={`Teksty (${sectionEntries.length})`} icon={<FilePlus2 />}>
                    {/* Expanded: sections as code blocks (same style as KB detail) */}
                    {isExpanded && sectionEntries.length > 0 && sectionEntries.map(([fieldName, section]) => {
                      const isEditing = editingField?.contentId === content.id && editingField?.field === fieldName;
                      const isRegen = regenerating === `${content.id}:${fieldName}`;
                      const displayText = section.edited || section.original || '';
                      const fieldTitle = getContentSectionTitle(fieldName, section);
                      const sectionKey = `${content.id}:${fieldName}`;
                      const isSectionExpanded = isEditing || Boolean(expandedContentSections[sectionKey]);

                      return (
                        <div key={fieldName} className="bg-surface-raised rounded-xl border border-border overflow-hidden">
                          {/* Section title bar */}
                          <div className={`flex items-center justify-between px-4 py-2.5 ${isSectionExpanded ? 'border-b border-border-subtle' : ''}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <button
                                type="button"
                                onClick={() => setExpandedContentSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                                className="flex items-center gap-2 min-w-0 text-left hover:opacity-85 transition-opacity"
                              >
                                <span className={`text-xs font-medium ${currentSection!.colorClass} truncate`}>{fieldTitle}</span>
                              </button>
                              {section.aiShortcode && (
                                <button
                                  type="button"
                                  onClick={() => copyShortcodeToClipboard(section.aiShortcode!, setCopiedId)}
                                  className="inline-flex items-center gap-1 text-[9px] text-accent-primary px-1.5 py-0.5 bg-accent-primary/10 rounded shrink-0 hover:bg-accent-primary/15 transition-colors cursor-pointer"
                                  title="Kopiuj shortcode"
                                >
                                  <span>[{section.aiShortcode}]</span>
                                  {copiedId === `shortcode:${section.aiShortcode}` ? <Check className="w-2.5 h-2.5 text-success" /> : <Copy className="w-2.5 h-2.5" />}
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => handleStartEdit(content.id, fieldName, displayText)}
                                className="p-1 text-text-muted hover:text-accent-primary transition-colors"
                                title="Edytuj"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              {!section.generatedByParser && (
                                <button
                                  onClick={() => handleRegenerateField(content.id, fieldName)}
                                  disabled={isRegen}
                                  className="p-1 text-text-muted hover:text-accent-primary transition-colors"
                                  title="Regeneruj"
                                >
                                  {isRegen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                </button>
                              )}
                              <button
                                onClick={() => setExpandedContentSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                                className="p-1 text-text-muted hover:text-accent-primary transition-colors"
                                title={isSectionExpanded ? 'Zwiń' : 'Rozwiń'}
                              >
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isSectionExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            </div>
                          </div>

                          {/* Code block or editor */}
                          {isSectionExpanded && (isEditing ? (
                            <div className="p-4">
                              <div className="relative">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(editValue);
                                    setCopiedId(sectionKey);
                                    setTimeout(() => setCopiedId(null), 2000);
                                  }}
                                  className="absolute right-6 top-2 z-10 p-1.5 text-text-muted hover:text-accent-primary bg-surface-base/80 border border-border-subtle rounded-md transition-colors"
                                  title="Kopiuj tekst"
                                >
                                  {copiedId === sectionKey ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                                </button>
                                <textarea
                                  ref={textareaRef}
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  className="w-full min-h-[200px] bg-surface-hover/80 border border-accent-primary/30 rounded-lg p-3 pr-14 text-xs text-text-primary focus:outline-none focus:border-accent-primary/50 resize-y font-mono"
                                />
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={handleSaveEdit}
                                  disabled={saving}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-text-primary bg-accent-primary/20 border border-accent-primary/30 rounded-lg hover:bg-accent-primary/30"
                                >
                                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                  Zapisz
                                </button>
                                <button onClick={() => setEditingField(null)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary">
                                  <X className="w-3 h-3" />
                                  Anuluj
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4">
                              <div className="relative">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(displayText);
                                    setCopiedId(sectionKey);
                                    setTimeout(() => setCopiedId(null), 2000);
                                  }}
                                  className="absolute right-6 top-2 z-10 p-1.5 text-text-muted hover:text-accent-primary bg-surface-base/80 border border-border-subtle rounded-md transition-colors"
                                  title="Kopiuj tekst"
                                >
                                  {copiedId === sectionKey ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                                </button>
                                <pre className="bg-surface-base/60 rounded-lg p-4 pr-14 text-xs text-text-secondary whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto font-mono">
                                  {displayText}
                                </pre>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}

                    {isExpanded && sectionEntries.length === 0 && (
                      <div className="bg-surface-raised rounded-xl border border-border p-8 text-center">
                        <p className="text-xs text-text-muted">Brak sekcji - generowanie nie zostało ukończone.</p>
                        {content.type === 'QUIZ' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-4"
                            onClick={() => onNewContent('QUIZ', contentGenerationInputs)}
                            disabled={generatingContent === 'QUIZ'}
                            leftIcon={generatingContent === 'QUIZ' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          >
                            Wygeneruj ponownie
                          </Button>
                        )}
                      </div>
                    )}

                    </ContentResourceSection>}

                    {/* Zasoby produkcyjne skryptu wideo (łańcuch: sceny → b-rolle → brief) */}
                    {isExpanded && VIDEO_AD_CONTENT_TYPES.has(content.type) && sectionEntries.length > 0 && (
                      <VideoAssetsPanel productId={productId} contentId={content.id} contentType={content.type} strategy={content.strategy || null} />
                    )}

                    {/* Kreacje reklam statycznych (shortcody + generowanie obrazów gpt-image) */}
                    {isExpanded && (content.type === 'AD_GRAPHIC' || content.type === 'AD_CREATIVE') && (
                      <AdCreativesPanel productId={productId} contentId={content.id} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Locked state for content when no KB */}
        {activeTab?.kind === 'content' && !hasCompleteKb && (
          <div className="bg-surface-raised rounded-xl border border-border p-8 text-center">
            <Lock className="w-6 h-6 text-text-muted mx-auto mb-2" />
            <p className="text-xs text-text-muted">Generowanie treści wymaga ukończonej bazy wiedzy.</p>
          </div>
        )}
      </div>

      {contentSourceModal && (
        <LabContentImportModal
          productId={productId}
          pipeline={contentSourceModal.pipeline}
          initialInputs={contentSourceModal.initialInputs}
          onScratch={() => {
            const selected = contentSourceModal;
            setContentSourceModal(null);
            onNewContent(selected.pipeline, selected.initialInputs);
          }}
          onClose={() => setContentSourceModal(null)}
          onComplete={() => {
            setContentSourceModal(null);
            onReload();
          }}
        />
      )}

      {campaignContent && (
        <CampaignModal
          productId={productId}
          contentId={campaignContent.id}
          contentTitle={campaignContent.title || getDefaultTitle(product.name, campaignContent.type, campaignContent.version)}
          onStarted={() => {
            setCampaignContent(null);
            onReload();
          }}
          onClose={() => setCampaignContent(null)}
        />
      )}

      {iterateVariantContent && (
        <IterateVariantModal
          productId={productId}
          contentId={iterateVariantContent.id}
          contentTitle={iterateVariantContent.title || getDefaultTitle(product.name, iterateVariantContent.type, iterateVariantContent.version)}
          contentType={iterateVariantContent.type}
          strategy={iterateVariantContent.strategy || null}
          onStarted={() => {
            setIterateVariantContent(null);
            onReload();
          }}
          onClose={() => setIterateVariantContent(null)}
        />
      )}

      {showReferenceBatchConfigurator && (
          <LabConfiguratorShell
            title="Konfiguracja zdjęć referencyjnych"
            description="Wybierz źródło stylu kolejnych zdjęć. System przygotuje też brakujące bazowe referencje."
            icon={<Upload className="w-6 h-6" />}
            onClose={() => setShowReferenceBatchConfigurator(false)}
            closeDisabled={savingReferenceStyle}
            maxWidthClassName="max-w-6xl"
            footer={(
              <div className="flex items-center justify-between gap-4">
                {generatingReferenceImages ? (
                  <Button
                    variant="danger"
                    onClick={handleStopReferenceImages}
                    loading={stoppingReferenceImages}
                    disabled={referenceGenerationProgress.generated < 1 || !referenceGenerationRunId}
                    leftIcon={<Square />}
                  >
                    Przerwij generowanie
                  </Button>
                ) : (
                  <Button
                    onClick={handleGenerateReferenceImages}
                    loading={savingReferenceStyle}
                    disabled={!referenceStyleId || (missingReferenceImageKinds.includes('PRODUCT') && referenceScreenshotFiles.length === 0)}
                    leftIcon={<RefreshCw />}
                  >
                    {missingReferenceImageKinds.length ? `Zapisz styl i generuj (${missingReferenceImageKinds.length})` : 'Zapisz źródło stylu'}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => setShowReferenceBatchConfigurator(false)}
                  disabled={savingReferenceStyle}
                >
                  {generatingReferenceImages ? 'Zamknij i obserwuj postęp' : 'Anuluj'}
                </Button>
              </div>
            )}
          >
            {referenceImageError && (
              <Alert variant="danger" title="Nie udało się wykonać operacji" onClose={() => setReferenceImageError(null)}>
                {referenceImageError}
              </Alert>
            )}

            {generatingReferenceImages && referenceGenerationProgress.total > 0 && (
              <div className="space-y-4 rounded-xl border border-accent-primary/25 bg-accent-primary/[0.06] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Postęp generowania</p>
                    <p className="mt-1 text-xs text-text-muted">
                      {referenceGenerationProgress.message || 'Gotowe pozycje pojawią się poniżej.'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-accent-primary">
                    {referenceGenerationProgress.current}/{referenceGenerationProgress.total}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-hover">
                  <div
                    className="h-full rounded-full bg-accent-primary transition-all duration-500"
                    style={{ width: `${Math.round((referenceGenerationProgress.current / referenceGenerationProgress.total) * 100)}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {referenceGenerationTargetKinds.map(kind => {
                    const image = referenceImagesByKind.get(kind);
                    const imageUrl = image
                      ? labApi.getReferenceImageFileUrl(productId, image.id, image.updatedAt || image.createdAt)
                      : null;
                    return (
                      <div key={kind} className="overflow-hidden rounded-lg border border-border-subtle bg-surface-raised">
                        <div className="aspect-square bg-surface-base/60 flex items-center justify-center">
                          {imageUrl ? (
                            <img src={imageUrl} alt={REFERENCE_IMAGE_LABELS[kind]} className="h-full w-full object-cover" />
                          ) : (
                            <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
                          )}
                        </div>
                        <div className="p-2 text-center text-[10px] font-medium text-text-secondary">
                          {REFERENCE_IMAGE_LABELS[kind]}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {referenceGenerationProgress.generated < 1 && (
                  <p className="text-[10px] text-text-muted">Po zapisaniu pierwszej grafiki możesz ocenić wynik i przerwać pozostałe generowanie.</p>
                )}
              </div>
            )}

            {productImageStylesQuery.isError && <Alert variant="danger" title="Nie udało się pobrać kart stylu">Zamknij i otwórz konfigurator ponownie.</Alert>}
            <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-text-primary">Screenshoty produktu</h4>
                  <p className="text-xs text-text-muted mt-1">
                    {referenceGenerationTargetKinds.includes('PRODUCT')
                      ? 'Screenshoty są potrzebne do przygotowania brakującej referencji produktu.'
                      : 'Screenshoty są opcjonalne. Istniejące zdjęcia produktu i opakowania zostaną użyte do brakujących pozycji.'}
                  </p>
                </div>

                <label
                  onDragOver={event => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = generatingReferenceImages ? 'none' : 'copy';
                    if (!generatingReferenceImages) setDraggingReferenceScreenshots(true);
                  }}
                  onDragLeave={event => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingReferenceScreenshots(false);
                  }}
                  onDrop={event => {
                    event.preventDefault();
                    setDraggingReferenceScreenshots(false);
                    handleReferenceScreenshotChange(event.dataTransfer.files);
                  }}
                  className={`block rounded-xl border border-dashed px-4 py-10 text-center transition-colors ${generatingReferenceImages ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-accent-primary/55'} ${draggingReferenceScreenshots ? 'border-accent-primary bg-accent-primary/15' : 'border-accent-primary/30 bg-accent-primary/5'}`}
                >
                  <Upload className="w-7 h-7 text-accent-primary mx-auto mb-3" />
                  <span className="block text-sm font-semibold text-text-primary">Przeciągnij screenshoty produktu lub wybierz pliki</span>
                  <span className="block text-xs text-text-muted mt-1">JPG, PNG albo WEBP, maksymalnie 3 pliki</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    className="hidden"
                    disabled={generatingReferenceImages}
                    onChange={event => {
                      handleReferenceScreenshotChange(event.target.files);
                      event.target.value = '';
                    }}
                  />
                </label>

                {referenceScreenshotFiles.length > 0 && (
                  <div className="space-y-2">
                    {referenceScreenshotFiles.map((file, index) => (
                      <div key={`${file.name}:${index}`} className="flex items-center justify-between gap-3 rounded-lg bg-surface-hover/40 border border-border-subtle px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs text-text-primary truncate">{file.name}</p>
                          <p className="text-[10px] text-text-muted">{Math.round(file.size / 1024)} KB</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setReferenceScreenshotFiles(prev => prev.filter((_, itemIndex) => itemIndex !== index))}
                          className="p-1.5 text-text-muted hover:text-danger rounded-md hover:bg-surface-hover transition-colors"
                          title="Usuń screenshot"
                          disabled={generatingReferenceImages}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <ReferenceImageStylePicker styleId={referenceStyleId} onChange={setReferenceStyleId} cards={productImageStyleCards}
                  loading={loadingProductImageStyles} disabled={generatingReferenceImages || savingReferenceStyle} photo={referenceStylePhoto} photoUrl={productStyleReferenceImageUrl}
                  onPhotoChange={file => {
                    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 12 * 1024 * 1024) { setReferenceImageError('Wybierz zdjęcie JPG, PNG lub WEBP do 12 MB.'); return; }
                    setReferenceStylePhoto(file); setReferenceImageError(null);
                  }} />
                {referenceStyleId !== PHOTO_STYLE_ID && <>
                <div>
                  <h4 className="text-sm font-semibold text-text-primary">Styl typografii</h4>
                  <p className="text-xs text-text-muted mt-1">
                    {missingReferenceImageKinds.includes('BEFORE_AFTER')
                      ? 'Ten styl zostanie użyty tylko dla brakującej planszy typografii.'
                      : 'Plansza typografii jest już gotowa i nie zostanie zmieniona.'}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {TYPOGRAPHY_STYLE_OPTIONS.map(option => (
                    <TypographyStylePreviewCard
                      key={option.id}
                      option={option}
                      selected={referenceTypographyStyle === option.id}
                      disabled={generatingReferenceImages || savingReferenceStyle || !missingReferenceImageKinds.includes('BEFORE_AFTER')}
                      onClick={() => setReferenceTypographyStyle(option.id)}
                    />
                  ))}
                </div>
                </>}
              </div>
            </div>
          </LabConfiguratorShell>
      )}

      {bundleConfigurator && <LabConfiguratorShell
        title={REFERENCE_IMAGE_LABELS[bundleConfigurator.kind]}
        description={bundleConfigurator.file ? 'Potwierdź liczbę sztuk na wgrywanym zdjęciu pakietu.' : 'Ustal liczbę sztuk i opcjonalne wskazówki do generowania pakietu.'}
        icon={<ImageIcon className="h-6 w-6" />}
        onClose={() => setBundleConfigurator(null)}
        closeDisabled={Boolean(generatingBundleKind || uploadingReferenceKind)}
        maxWidthClassName="max-w-lg"
        footer={<div className="flex items-center justify-between gap-3">
          <Button onClick={() => {
            const quantity = Number(customBundleQuantity);
            if (bundleConfigurator.file) void handleUploadSingleReferenceImage(bundleConfigurator.kind, bundleConfigurator.file, quantity);
            else void handleGenerateBundleReference(bundleConfigurator.kind, quantity, bundleConfigurator.kind === 'BUNDLE_CUSTOM');
          }} loading={Boolean(generatingBundleKind || uploadingReferenceKind)}
            disabled={!Number.isInteger(Number(customBundleQuantity)) || Number(customBundleQuantity) < 1 || Number(customBundleQuantity) > 99}>
            {bundleConfigurator.file ? 'Wgraj zdjęcie' : 'Generuj pakiet'}
          </Button>
          <Button variant="secondary" onClick={() => setBundleConfigurator(null)} disabled={Boolean(generatingBundleKind || uploadingReferenceKind)}>Anuluj</Button>
        </div>}
      >
        {referenceImageError && <Alert variant="danger" title="Nie udało się zapisać pakietu">{referenceImageError}</Alert>}
        <label className="block space-y-1.5">
          <span className="text-sm text-text-secondary">Liczba sztuk</span>
          <input type="number" min={1} max={99} value={customBundleQuantity} onChange={event => setCustomBundleQuantity(event.target.value)}
            className="input w-full text-sm" disabled={bundleConfigurator.kind !== 'BUNDLE_CUSTOM' || Boolean(generatingBundleKind || uploadingReferenceKind)} />
        </label>
        {!bundleConfigurator.file && <label className="block space-y-1.5">
          <span className="text-sm text-text-secondary">Dodatkowa wskazówka dla AI</span>
          <textarea value={referenceRegenerationInstruction} onChange={event => setReferenceRegenerationInstruction(event.target.value)} maxLength={2000}
            placeholder="Np. ułóż opakowania obok siebie, pokaż etykiety od przodu..." className="input min-h-[120px] w-full resize-y text-sm" disabled={Boolean(generatingBundleKind)} />
        </label>}
      </LabConfiguratorShell>}

      {referenceTypographyModal && (
        <LabConfiguratorShell
          title={referenceTypographyModal.image ? 'Zmień planszę typografii' : 'Generuj planszę typografii'}
          description="Wybierz ponownie gotowy styl albo wygeneruj planszę według własnego promptu."
          icon={<ImageIcon className="w-6 h-6" />}
          onClose={closeReferenceTypographyModal}
          closeDisabled={Boolean(regeneratingReferenceImageId)}
          maxWidthClassName="max-w-6xl"
          footer={(
            <div className="flex items-center justify-between gap-3">
              <Button
                onClick={handleSaveReferenceTypography}
                loading={Boolean(regeneratingReferenceImageId)}
                leftIcon={<RefreshCw />}
              >
                {referenceTypographyMode === 'prompt'
                  ? 'Generuj z promptu'
                  : referenceTypographyModal.image ? 'Zapisz wybrany styl' : 'Generuj typografię'}
              </Button>
              <Button
                variant="secondary"
                onClick={closeReferenceTypographyModal}
                disabled={Boolean(regeneratingReferenceImageId)}
              >
                Anuluj
              </Button>
            </div>
          )}
        >
          {referenceImageError && (
            <Alert variant="danger" title="Nie udało się zapisać typografii" onClose={() => setReferenceImageError(null)}>
              {referenceImageError}
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setReferenceTypographyMode('style')}
              disabled={Boolean(regeneratingReferenceImageId)}
              className={`rounded-xl border p-4 text-left transition-colors disabled:opacity-60 ${
                referenceTypographyMode === 'style'
                  ? 'border-accent-primary bg-accent-primary/10'
                  : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'
              }`}
            >
              <span className="text-sm font-semibold text-text-primary">Wybierz gotowy styl</span>
              <span className="mt-1 block text-xs text-text-muted">Ponownie wybierz jedną z wizualnych kart typografii.</span>
            </button>
            <button
              type="button"
              onClick={() => setReferenceTypographyMode('prompt')}
              disabled={Boolean(regeneratingReferenceImageId)}
              className={`rounded-xl border p-4 text-left transition-colors disabled:opacity-60 ${
                referenceTypographyMode === 'prompt'
                  ? 'border-accent-primary bg-accent-primary/10'
                  : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'
              }`}
            >
              <span className="text-sm font-semibold text-text-primary">Własny prompt</span>
              <span className="mt-1 block text-xs text-text-muted">Wygeneruj nową planszę przez AI według własnych wskazówek.</span>
            </button>
          </div>

          {referenceTypographyMode === 'style' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {TYPOGRAPHY_STYLE_OPTIONS.map(option => (
                <TypographyStylePreviewCard
                  key={option.id}
                  option={option}
                  selected={referenceTypographyStyle === option.id}
                  disabled={Boolean(regeneratingReferenceImageId)}
                  onClick={() => setReferenceTypographyStyle(option.id)}
                />
              ))}
            </div>
          ) : (
            <label className="space-y-1.5 block">
              <span className="text-sm text-text-secondary">Prompt typografii</span>
              <textarea
                value={referenceRegenerationInstruction}
                onChange={event => setReferenceRegenerationInstruction(event.target.value)}
                placeholder="Np. minimalistyczna typografia medyczna, duży granatowy nagłówek, jasne tło, czytelny font bezszeryfowy, układ zoptymalizowany pod telefon..."
                maxLength={2000}
                className="input w-full min-h-[140px] resize-y text-sm"
                disabled={Boolean(regeneratingReferenceImageId)}
              />
              <span className="block text-[10px] text-text-muted">Ten tryb uruchamia generator AI i zastępuje obecną planszę dopiero po zapisaniu nowego wyniku.</span>
            </label>
          )}
        </LabConfiguratorShell>
      )}

      {referenceImageToRegenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              if (!regeneratingReferenceImageId) closeReferenceRegenerationModal();
            }}
          />
          <div className="relative w-full max-w-lg bg-surface-raised border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">
                  {referenceImageToRegenerate.kind === 'BEFORE_AFTER' ? 'Zmień planszę typografii' : 'Regeneruj zdjęcie referencyjne'}
                </h3>
                <p className="text-xs text-text-muted mt-0.5">
                  {referenceImageDisplayTitle(referenceImageToRegenerate)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeReferenceRegenerationModal}
                disabled={Boolean(regeneratingReferenceImageId)}
                className="p-1.5 text-text-muted hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-xl border border-border-subtle bg-surface-hover/40 px-4 py-3">
                <div className="flex items-start gap-3">
                  <Upload className="w-4 h-4 text-accent-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-text-primary">
                      {referenceImageToRegenerate.kind === 'BEFORE_AFTER'
                        ? `Wybrany styl typografii: ${selectedTypographyStyleTitle(referenceTypographyStyle)}`
                        : referenceImageToRegenerate.kind === 'PRODUCT' && referenceScreenshotFiles.length === 0
                          ? 'Obecne zdjęcie produktu'
                          : `Screenshoty produktu: ${referenceScreenshotFiles.length}/3`}
                    </div>
                    <div className="text-xs text-text-muted mt-1">
                      {referenceImageToRegenerate.kind === 'PRODUCT'
                        ? referenceScreenshotFiles.length > 0
                          ? 'Regeneracja użyje dodanych screenshotów produktu oraz Twojej instrukcji.'
                          : 'Regeneracja użyje obecnego zdjęcia referencyjnego produktu oraz Twojej instrukcji. Nie musisz dodawać nowych screenshotów.'
                        : referenceImageToRegenerate.kind === 'PACKAGING'
                        ? 'Regeneracja opakowania użyje aktualnego produktu [lab_ref_product] oraz opcjonalnie dodanych screenshotów.'
                        : referenceImageToRegenerate.kind === 'COMBO'
                        ? 'Regeneracja combo użyje aktualnych referencji [lab_ref_product] oraz [lab_ref_packaging].'
                        : 'Typografia nie jest generowana przez AI. System zapisze jako referencję wybraną poniżej kartę stylu.'}
                    </div>
                  </div>
                </div>
              </div>

              {referenceImageToRegenerate.kind === 'BEFORE_AFTER' && (
                <div className="space-y-2">
                  <span className="text-xs text-text-secondary">Styl fontu</span>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {TYPOGRAPHY_STYLE_OPTIONS.map(option => {
                      const selected = referenceTypographyStyle === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setReferenceTypographyStyle(option.id)}
                          disabled={Boolean(regeneratingReferenceImageId)}
                          className={`rounded-lg border p-3 text-left transition-colors disabled:opacity-60 ${
                            selected
                              ? 'border-accent-primary bg-accent-primary/10'
                              : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-4 h-4 rounded border border-border-subtle shrink-0 ${option.previewClass}`} />
                            <span className="text-xs font-semibold text-text-primary">{option.title}</span>
                          </div>
                          <p className="text-[10px] text-text-muted mt-1.5 leading-relaxed line-clamp-2">{option.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <label className="space-y-1.5 block">
                <span className="text-xs text-text-secondary">
                  {referenceImageToRegenerate.kind === 'BEFORE_AFTER' ? 'Notatka do zapisu' : 'Dodatkowa instrukcja dla AI'}
                </span>
                <textarea
                  value={referenceRegenerationInstruction}
                  onChange={event => setReferenceRegenerationInstruction(event.target.value)}
                  placeholder={referenceImageToRegenerate.kind === 'BEFORE_AFTER'
                    ? 'Opcjonalna notatka. Wybrany styl typografii zostanie zapisany jako gotowa plansza referencyjna.'
                    : 'Np. mniej kontrastowe światło, bardziej luksusowy wygląd opakowania, inny kąt produktu...'}
                  maxLength={2000}
                  className="input w-full min-h-[120px] resize-y text-sm"
                  disabled={Boolean(regeneratingReferenceImageId)}
                />
              </label>

              <div className="flex items-center justify-between gap-3 pt-2">
                <Button
                  onClick={handleRegenerateReferenceImage}
                  loading={Boolean(regeneratingReferenceImageId)}
                  leftIcon={<RefreshCw />}
                >
                  {referenceImageToRegenerate.kind === 'BEFORE_AFTER' ? 'Zapisz styl' : 'Regeneruj'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={closeReferenceRegenerationModal}
                  disabled={Boolean(regeneratingReferenceImageId)}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {productImagesGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              if (!startingProductImagesGeneration) closeProductImagesGenerateModal();
            }}
          />
          <div className="relative w-full max-w-5xl max-h-[calc(100vh-2rem)] bg-surface-raised border border-border rounded-xl shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border-subtle">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-accent-secondary/10 border border-accent-secondary/20 flex items-center justify-center shrink-0">
                  <ImageIcon className="w-5 h-5 text-accent-secondary" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-text-primary">
                    {(productImagesByContentId.get(productImagesGenerateModal.id) || []).some(image => Boolean(image.driveFileId))
                      ? 'Regeneruj grafiki produktowe'
                      : 'Generuj grafiki produktowe'}
                  </h3>
                  <p className="text-xs text-text-muted mt-0.5 truncate">
                    {productImagesGenerateModal.title || 'Treść sprzedażowa'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeProductImagesGenerateModal}
                disabled={startingProductImagesGeneration}
                className="p-1.5 text-text-muted hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
              {imageQualityControl(startingProductImagesGeneration)}
              {productImageError && (
                <Alert variant="danger" title="Nie udało się uruchomić galerii" onClose={() => setProductImageError(null)}>
                  {productImageError}
                </Alert>
              )}

              {(productImagesByContentId.get(productImagesGenerateModal.id) || []).some(image => Boolean(image.driveFileId)) ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-medium text-text-primary">Wybierz grafiki do regeneracji</h4>
                      <p className="mt-0.5 text-xs text-text-muted">Pozostałe grafiki nie zostaną zmienione.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const allIds = (productImagesByContentId.get(productImagesGenerateModal.id) || []).map(image => image.id);
                        setSelectedProductImageIds(selectedProductImageIds.length === allIds.length ? [] : allIds);
                      }}
                      disabled={startingProductImagesGeneration}
                      className="text-xs font-medium text-accent-primary hover:text-accent-primary/80 disabled:opacity-50"
                    >
                      {selectedProductImageIds.length === (productImagesByContentId.get(productImagesGenerateModal.id) || []).length
                        ? 'Odznacz wszystkie'
                        : 'Zaznacz wszystkie'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {(productImagesByContentId.get(productImagesGenerateModal.id) || []).map(image => {
                      const selected = selectedProductImageIds.includes(image.id);
                      return (
                        <button
                          key={image.id}
                          type="button"
                          onClick={() => setSelectedProductImageIds(previous => (
                            previous.includes(image.id)
                              ? previous.filter(id => id !== image.id)
                              : [...previous, image.id]
                          ))}
                          disabled={startingProductImagesGeneration}
                          className={`overflow-hidden rounded-xl border text-left transition-colors ${selected ? 'border-accent-primary bg-accent-primary/10' : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'}`}
                        >
                          <div className="relative aspect-square bg-surface-base">
                            {image.driveFileId ? <img
                              src={labApi.getProductImageFileUrl(productId, image.id, image.updatedAt || image.createdAt)}
                              alt=""
                              className="h-full w-full object-cover"
                            /> : <div className="h-full flex items-center justify-center text-xs text-text-muted">Brak grafiki</div>}
                            <span className={`absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border ${selected ? 'border-accent-primary bg-accent-primary text-white' : 'border-white/70 bg-black/40 text-transparent'}`}>
                              <Check className="h-3 w-3" />
                            </span>
                          </div>
                          <div className="p-2 text-xs font-medium text-text-primary line-clamp-2">{image.title}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
              <>
              <label className="space-y-1.5 block">
                <span className="text-sm text-text-secondary">Język tekstów na grafikach</span>
                <Select
                  value={productImageGenerationLanguage}
                  onChange={event => setProductImageGenerationLanguage(event.target.value)}
                  className="input w-full text-sm"
                  disabled={startingProductImagesGeneration}
                >
                  {PRODUCT_IMAGE_LANGUAGE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </label>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-text-secondary">{savedProductImageStyleId ? 'Zapisane źródło stylu' : 'Karta stylu grafik'}</span>
                  {loadingProductImageStyles && <Loader2 className="w-4 h-4 animate-spin text-text-muted" />}
                </div>
                {savedProductImageStyleId && <p className="text-xs text-text-muted">Wybór ze Zdjęć referencyjnych obowiązuje dla całej galerii. Zmienisz go przez „Konfiguruj styl” w tamtej sekcji.</p>}
                {productImageStylesQuery.isError && <p className="text-xs text-danger">Nie udało się pobrać kart stylu. Otwórz konfigurator ponownie.</p>}
                {selectableProductImageStyleOptions.length === 0 && productImageStyleOptions.length === 0 && !loadingProductImageStyles ? (
                  <div className="rounded-xl border border-border-subtle bg-surface-hover/40 p-4 text-sm text-text-muted">
                    Brak aktywnych kart stylu. System użyje domyślnej spójności z referencjami produktu.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {productImageStyleOptions.map(card => {
                      const selected = productImageStyleId === card.id;
                      const locked = Boolean(card.isLocked);
                      return (
                        <button
                          key={card.id}
                          type="button"
                          onClick={() => {
                            if (locked || startingProductImagesGeneration) return;
                            setProductImageStyleId(card.id);
                          }}
                          disabled={locked || startingProductImagesGeneration}
                          className={`overflow-hidden rounded-xl border text-left transition-colors ${
                            selected
                              ? 'border-accent-primary bg-accent-primary/10'
                              : locked
                                ? 'border-border-subtle bg-surface-hover/20 opacity-70 cursor-not-allowed'
                                : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'
                          }`}
                        >
                          <div className="relative aspect-[16/9] bg-surface-base flex items-center justify-center overflow-hidden">
                            {card.imageUrl ? (
                              <img src={card.imageUrl} alt={card.name} className="w-full h-full object-cover" />
                            ) : (
                              <div
                                className="w-full h-full flex items-center justify-center"
                                style={{ backgroundColor: card.accentColor || 'var(--surface-hover)' }}
                              >
                                <Palette className="w-8 h-8 text-white/85" />
                              </div>
                            )}
                            {locked && (
                              <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
                                <span className="w-10 h-10 rounded-full bg-surface-raised/95 border border-border-subtle flex items-center justify-center text-text-muted">
                                  <Lock className="w-4 h-4" />
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="flex items-center gap-2">
                              <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-accent-primary' : 'border-border-strong'}`}>
                                {selected && <span className="w-2 h-2 rounded-full bg-accent-primary" />}
                              </span>
                              <span className="text-sm font-semibold text-text-primary">{card.name}</span>
                              {locked && <Lock className="w-3.5 h-3.5 text-text-muted ml-auto" />}
                            </div>
                            {card.description && (
                              <p className="text-xs text-text-muted mt-1.5 leading-relaxed">{card.description}</p>
                            )}
                            {locked && card.lockReason && (
                              <p className="text-[10px] text-text-disabled mt-2 leading-relaxed">{card.lockReason}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border-subtle">
              <Button
                onClick={handleStartProductImagesGeneration}
                loading={startingProductImagesGeneration}
                disabled={
                  loadingProductImageStyles
                  || !canGeneratePageImages(productImagesGenerateModal)
                  || ((productImagesByContentId.get(productImagesGenerateModal.id) || []).some(image => Boolean(image.driveFileId)) && selectedProductImageIds.length === 0)
                }
                leftIcon={<ImageIcon />}
              >
                {(productImagesByContentId.get(productImagesGenerateModal.id) || []).some(image => Boolean(image.driveFileId))
                  ? `Regeneruj wybrane${selectedProductImageIds.length > 0 ? ` (${selectedProductImageIds.length})` : ''}`
                  : 'Generuj grafiki'}
              </Button>
              <Button
                variant="secondary"
                onClick={closeProductImagesGenerateModal}
                disabled={startingProductImagesGeneration}
              >
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}

      {productImagePromptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeProductImagePromptModal}
          />
          <div className="relative w-full max-w-3xl bg-surface-raised border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Prompt zdjęcia produktowego</h3>
                <p className="text-xs text-text-muted mt-0.5">{productImagePromptModal.title}</p>
              </div>
              <button
                type="button"
                onClick={closeProductImagePromptModal}
                disabled={Boolean(regeneratingProductImageId)}
                className="p-1.5 text-text-muted hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {imageQualityControl(Boolean(regeneratingProductImageId))}
              <ProductImageReferences productId={productId} image={productImagePromptModal} />
              {productImageError && (
                <Alert variant="danger" className="text-xs">
                  {productImageError}
                </Alert>
              )}
              <label className="space-y-1.5 block">
                <span className="text-xs text-text-secondary">Finalny prompt przekazany do generatora obrazu</span>
                <textarea
                  value={productImagePromptValue}
                  onChange={event => setProductImagePromptValue(event.target.value)}
                  className="input w-full min-h-[360px] resize-y text-xs font-mono leading-relaxed"
                  disabled={Boolean(regeneratingProductImageId)}
                />
              </label>
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border-subtle">
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSaveProductImagePrompt}
                  loading={savingProductImagePrompt}
                  disabled={!productImagePromptValue.trim() || Boolean(regeneratingProductImageId)}
                  leftIcon={<Save />}
                >
                  Zapisz
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleRegenerateProductImage}
                  loading={Boolean(regeneratingProductImageId)}
                  disabled={!productImagePromptValue.trim() || savingProductImagePrompt}
                  leftIcon={<RefreshCw />}
                >
                  Regeneruj
                </Button>
              </div>
              <Button
                variant="secondary"
                onClick={closeProductImagePromptModal}
                disabled={Boolean(regeneratingProductImageId) || savingProductImagePrompt}
              >
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}

      {productImagePreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setProductImagePreviewModal(null)} />
          <div className="relative w-full max-w-5xl bg-surface-raised border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Podgląd zdjęcia produktowego</h3>
                <p className="text-xs text-text-muted mt-0.5">{productImagePreviewModal.title}</p>
              </div>
              <button
                type="button"
                onClick={() => setProductImagePreviewModal(null)}
                className="p-1.5 text-text-muted hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[78vh] overflow-auto bg-black/30 p-4">
              <img
                src={labApi.getProductImageFileUrl(productId, productImagePreviewModal.id, productImagePreviewModal.updatedAt || productImagePreviewModal.createdAt)}
                alt={productImagePreviewModal.title}
                className="mx-auto max-h-[72vh] w-auto max-w-full rounded-lg object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {productImageEditModal && (
        <Modal
          isOpen
          onClose={() => { if (!editingProductImageId) setProductImageEditModal(null); }}
          title="Modyfikuj zdjęcie produktowe"
          description={productImageEditModal.title}
          size="lg"
          closeOnBackdrop={!editingProductImageId}
          closeOnEscape={!editingProductImageId}
          footer={(
            <div className="flex items-center justify-between gap-3 ">
              <Button
                onClick={handleEditProductImage}
                loading={Boolean(editingProductImageId)}
                disabled={!productImageEditInstruction.trim() && productImageEditMode !== 'reference_rebuild'}
                leftIcon={<Wand2 />}
              >
                Generuj
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setProductImageEditModal(null);
                  setProductImageEditInstruction('');
                }}
                disabled={Boolean(editingProductImageId)}
              >
                Anuluj
              </Button>
            </div>
          )}
        >
          <div className="space-y-5">
            {productImageError && <Alert variant="danger" className="text-xs">{productImageError}</Alert>}
            <div className="flex justify-center rounded-xl border border-border-subtle bg-surface-hover/30 p-3">
              <img
                src={labApi.getProductImageFileUrl(productId, productImageEditModal.id, productImageEditModal.updatedAt || productImageEditModal.createdAt)}
                alt={productImageEditModal.title}
                className="h-44 w-full rounded-lg object-contain"
              />
            </div>
            {imageQualityControl(Boolean(editingProductImageId))}
            <div className="space-y-2">
              <p className="text-sm text-text-secondary">Dodatkowe zdjęcie referencyjne (opcjonalnie)</p>
              <div
                onDragOver={event => { event.preventDefault(); if (!editingProductImageId) setDraggingProductImageReference(true); }}
                onDragLeave={() => setDraggingProductImageReference(false)}
                onDrop={event => { event.preventDefault(); setDraggingProductImageReference(false); acceptProductImageReference(event.dataTransfer.files[0]); }}
                className={`rounded-xl border border-dashed p-4 text-center ${draggingProductImageReference ? 'border-accent-primary bg-accent-primary/10' : 'border-border bg-surface-hover/30'}`}
              >
                <input ref={productImageReferenceInput} type="file" accept="image/jpeg,image/png,image/webp" disabled={Boolean(editingProductImageId)}
                  className="sr-only" aria-label="Dodatkowe zdjęcie referencyjne"
                  onChange={event => { acceptProductImageReference(event.target.files?.[0]); event.target.value = ''; }} />
                <button type="button" disabled={Boolean(editingProductImageId)} onClick={() => productImageReferenceInput.current?.click()}
                  className="inline-flex items-center gap-2 text-sm font-medium text-accent-primary disabled:opacity-50">
                  <Upload className="h-5 w-5" />{productImageEditReference ? 'Zmień zdjęcie' : 'Wybierz zdjęcie'}
                </button>
                <p className="mt-2 text-xs text-text-muted">Przeciągnij zdjęcie tutaj lub wybierz je z dysku. JPG, PNG lub WEBP, do 12 MB.</p>
                {productImageEditReferenceUrl && (
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <img src={productImageEditReferenceUrl} alt="Dodatkowa referencja" className="h-16 w-16 rounded-lg object-contain" />
                    <Button variant="secondary" size="sm" disabled={Boolean(editingProductImageId)} onClick={() => setProductImageEditReference(null)}>Usuń załącznik</Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-text-muted">Tylko do tej edycji. Zdjęcie nie trafi do biblioteki i zostanie odłączone po zakończeniu próby.</p>
            </div>
            <label className="block space-y-1.5">
              <span className="text-sm text-text-secondary">Komentarz do modyfikacji</span>
              <textarea value={productImageEditInstruction} onChange={event => setProductImageEditInstruction(event.target.value)}
                placeholder="Opisz, co zmienić. Np. popraw kolor i kształt produktu według referencji, zachowując pozostałe elementy zdjęcia."
                className="input w-full min-h-[120px] resize-y text-sm" disabled={Boolean(editingProductImageId)} />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm text-text-secondary">Sposób poprawy</span>
              <Select value={productImageEditMode} disabled={Boolean(editingProductImageId)} onChange={event => setProductImageEditMode(event.target.value as 'local' | 'reference_rebuild')}>
                <option value="local">Edytuj istniejące zdjęcie</option>
                <option value="reference_rebuild">Odtwórz produkt z referencji</option>
              </Select>
              <p className="text-xs text-text-muted">{productImageEditMode === 'reference_rebuild'
                ? 'EcomFlow porówna to zdjęcie z referencjami i uwzględni Twoją uwagę, np. „zmniejsz plaster”. Sam przygotuje opis korekty i sceny. Generator dostanie właściwe referencje, a szczegóły kadru mogą się zmienić. Komentarz jest opcjonalny.'
                : 'Zachowuje zdjęcie jako bazę kompozycji. Do drobnych zmian tekstu, kadru i elementów sceny.'}</p>
            </label>
            <ProductImageReferences productId={productId} image={productImageEditModal} />
          </div>
        </Modal>
      )}

      <Modal
        isOpen={Boolean(updateModalContent)}
        onClose={closeUpdatePageModal}
        title="Aktualizuj stronę produktową"
        description={updateModalContent ? updateModalContent.title || getDefaultTitle(product.name, updateModalContent.type, updateModalContent.version) : undefined}
        size="lg"
        closeOnBackdrop={!updatingPageScope}
        closeOnEscape={!updatingPageScope}
        footer={(
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-muted">
              Aktualizacja użyje ostatniego produktu utworzonego z tej treści.
            </div>
            <Button variant="secondary" onClick={closeUpdatePageModal} disabled={Boolean(updatingPageScope)}>
              Zamknij
            </Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-border-subtle bg-surface-hover/30 p-3">
            <Checkbox
              checked={compressPageUpdateImages}
              onChange={event => setCompressPageUpdateImages(event.target.checked)}
              disabled={Boolean(updatingPageScope)}
              label="Kompresuj grafiki"
              description="Po zaznaczeniu grafiki są konwertowane do WebP. Po odznaczeniu wysyłamy oryginalne pliki."
            />
          </div>
          <div className="grid gap-3">
            {([
              {
                scope: 'all' as BuilderPageUpdateScope,
                title: 'Wszystko',
                description: 'Aktualizuj wszystkie treści i grafiki na stronie produktowej.',
                icon: RefreshCw,
              },
              {
                scope: 'content' as BuilderPageUpdateScope,
                title: 'Treści',
                description: 'Aktualizuj tylko treści na stronie produktowej.',
                icon: Copy,
              },
              {
                scope: 'graphics' as BuilderPageUpdateScope,
                title: 'Grafiki',
                description: 'Aktualizuj tylko grafiki na stronie produktowej.',
                icon: ImageIcon,
              },
            ]).map(option => {
              const Icon = option.icon;
              const isLoading = updatingPageScope === option.scope;
              return (
                <button
                  key={option.scope}
                  type="button"
                  onClick={() => handleUpdateBuilderPage(option.scope)}
                  disabled={Boolean(updatingPageScope)}
                  className="flex items-start gap-3 rounded-card border border-border-subtle bg-surface-hover/30 p-4 text-left transition-colors hover:border-accent-primary/40 hover:bg-accent-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-accent-primary/20 bg-accent-primary/10 text-accent-primary">
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-text-primary">{option.title}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-text-muted">{option.description}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {(updatingPageScope || pageUpdateProgress.total > 0) && (
            <div className="rounded-lg border border-border-subtle bg-surface-hover/30 px-3 py-3">
              <div className="mb-2 flex items-center justify-between text-xs text-text-secondary">
                <span>Postęp aktualizacji</span>
                <span>
                  {pageUpdateProgress.total
                    ? Math.round((pageUpdateProgress.current / pageUpdateProgress.total) * 100)
                    : 0}
                  %
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-surface-raised">
                <div
                  className="h-full rounded-full bg-accent-primary transition-all duration-500"
                  style={{
                    width: `${pageUpdateProgress.total
                      ? Math.min(100, (pageUpdateProgress.current / pageUpdateProgress.total) * 100)
                      : 5}%`,
                  }}
                />
              </div>
              <p className="mt-2 min-h-5 text-xs text-text-muted">
                {pageUpdateProgress.message || 'Przygotowuję aktualizację...'}
              </p>
            </div>
          )}

          {pageUpdateError && (
            <Alert variant="danger" className="text-xs">
              {pageUpdateError}
            </Alert>
          )}

          {updatedPage && (
            <div className="rounded-lg border border-success/20 bg-success/10 px-3 py-2 text-xs text-success space-y-2">
              <div>
                Zaktualizowano: <span className="font-medium">{updatedPage.page.title}</span>
                {updatedPage.wcProductId ? <span className="text-success/80"> #{updatedPage.wcProductId}</span> : null}
              </div>
              {updatedPage.missingShortcodes.length > 0 && (
                <div className="text-warning">
                  Nieuzupełnione shortcode'y: {updatedPage.missingShortcodes.map(code => `[${code}]`).join(', ')}
                </div>
              )}
              <a
                href={updatedPage.builderUrl}
                className="inline-flex items-center gap-1.5 text-accent-primary hover:underline"
              >
                Otwórz produkt w Kreatorze
                <ExternalLink className="w-3 h-3" />
              </a>
              {updatedPage.productBuilderUrl && (
                <a
                  href={updatedPage.productBuilderUrl}
                  className="ml-3 inline-flex items-center gap-1.5 text-accent-primary hover:underline"
                >
                  Otwórz Builder
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>
      </Modal>

      {pageModalContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPageModalContent(null)} />
          <div className="relative w-full max-w-lg bg-surface-raised border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">
                  {isProductBuilderConfig(builderPageConfig) ? 'Generuj produkt w Kreatorze' : 'Generuj stronę w Kreatorze'}
                </h3>
                <p className="text-xs text-text-muted mt-0.5">
                  {pageModalContent.title || getDefaultTitle(product.name, pageModalContent.type, pageModalContent.version)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPageModalContent(null)}
                className="p-1.5 text-text-muted hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {loadingPageOptions ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <label className="space-y-1.5">
                      <span className="text-xs text-text-secondary">Sklep</span>
                      <Select
                        value={pageForm.integrationId}
                        onChange={e => setPageForm(prev => ({ ...prev, integrationId: e.target.value }))}
                        disabled={brandStoreSelection.locked}
                        className="input w-full text-sm"
                      >
                        {engineStores.map(store => (
                          <option key={store.id} value={store.engineIntegrationId || ''}>
                            {store.name}
                          </option>
                        ))}
                      </Select>
                      {brandStoreSelection.locked && brandStoreSelection.storeName && (
                        <span className="mt-1 block text-[11px] text-success">
                          Sklep „{brandStoreSelection.storeName}” został wybrany automatycznie z marki produktu.
                        </span>
                      )}
                    </label>

                    {brandStoreSelection.warning && (
                      <Alert variant="warning" className="text-xs">
                        {brandStoreSelection.warning}
                      </Alert>
                    )}

                    <div className="rounded-xl border border-border-subtle bg-surface-hover/40 px-4 py-3">
                      <div className="flex items-start gap-3">
                        <Info className="w-4 h-4 text-accent-info shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-text-primary">
                            Typ strony: {builderPageConfig ? BUILDER_PAGE_TYPE_LABELS[builderPageConfig.pageType] : 'nieustawiony'}
                          </div>
                          <div className="text-xs text-text-muted mt-1">
                            {isProductBuilderConfig(builderPageConfig)
                              ? 'Produkt zostanie utworzony w zakładce Produkty, a techniczny szablon JSON wypełni jego stronę produktową.'
                              : 'Techniczny szablon JSON jest przypisany w konfiguracji pipeline w Panelu Super Admina.'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`grid grid-cols-1 gap-3 ${isProductBuilderConfig(builderPageConfig) ? '' : 'sm:grid-cols-2'}`}>
                    {!isProductBuilderConfig(builderPageConfig) && (
                      <label className="space-y-1.5">
                        <span className="text-xs text-text-secondary">Tytuł</span>
                        <input
                          type="text"
                          value={pageForm.title}
                          onChange={e => setPageForm(prev => ({ ...prev, title: e.target.value }))}
                          className="input w-full text-sm"
                        />
                      </label>
                    )}
                    <label className="space-y-1.5">
                      <span className="text-xs text-text-secondary">Slug</span>
                      <input
                        type="text"
                        value={pageForm.slug}
                        onChange={e => setPageForm(prev => ({ ...prev, slug: e.target.value }))}
                        className="input w-full text-sm"
                        placeholder="automatycznie"
                      />
                    </label>
                  </div>

                  <div className="rounded-xl border border-border-subtle bg-surface-hover/30 p-3">
                    <Toggle
                      size="sm"
                      checked={pageForm.status === 'PUBLISHED'}
                      onChange={e => setPageForm(prev => ({ ...prev, status: e.target.checked ? 'PUBLISHED' : 'DRAFT' }))}
                      label="Opublikuj po wygenerowaniu"
                      description={isProductBuilderConfig(builderPageConfig)
                        ? 'Gdy wyłączone, produkt zostanie zapisany jako szkic.'
                        : 'Gdy wyłączone, strona zostanie zapisana jako szkic.'}
                    />
                  </div>

                  <div className="rounded-xl border border-border-subtle bg-surface-hover/30 p-3">
                    <Checkbox
                      checked={pageForm.compressImages}
                      onChange={event => setPageForm(prev => ({ ...prev, compressImages: event.target.checked }))}
                      disabled={generatingPage}
                      label="Kompresuj grafiki"
                      description="Po zaznaczeniu grafiki są konwertowane do WebP. Po odznaczeniu wysyłamy oryginalne pliki."
                    />
                  </div>

                  {pageGenerationError && (
                    <Alert variant={hasBuilderTemplate(builderPageConfig) ? 'danger' : 'warning'} className="text-xs">
                      {pageGenerationError}
                    </Alert>
                  )}

                  {generatedPage && (
                    <div className="px-3 py-2 rounded-lg bg-success/10 border border-success/20 text-xs text-success space-y-2">
                      <div>
                        {generatedPage.resourceType === 'product' ? 'Produkt utworzony' : 'Strona utworzona'}: <span className="font-medium">{generatedPage.page.title}</span>
                        {generatedPage.wcProductId ? <span className="text-success/80"> #{generatedPage.wcProductId}</span> : null}
                      </div>
                      {generatedPage.missingShortcodes.length > 0 && (
                        <div className="text-warning">
                          Nieuzupełnione shortcode'y: {generatedPage.missingShortcodes.map(code => `[${code}]`).join(', ')}
                        </div>
                      )}
                      <a
                        href={generatedPage.builderUrl}
                        className="inline-flex items-center gap-1.5 text-accent-primary hover:underline"
                      >
                        {generatedPage.resourceType === 'product' ? 'Otwórz produkt w Kreatorze' : 'Otwórz w Kreatorze'}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {generatedPage.productBuilderUrl && (
                        <a
                          href={generatedPage.productBuilderUrl}
                          className="ml-3 inline-flex items-center gap-1.5 text-accent-primary hover:underline"
                        >
                          Otwórz Builder
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border-subtle">
              <button
                type="button"
                onClick={handleGenerateBuilderPage}
                disabled={loadingPageOptions || generatingPage || !pageForm.integrationId || !hasBuilderTemplate(builderPageConfig)}
                className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
              >
                {generatingPage ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus2 className="w-4 h-4" />}
                {isProductBuilderConfig(builderPageConfig) ? 'Generuj produkt' : 'Generuj stronę'}
              </button>
              <button
                type="button"
                onClick={() => setPageModalContent(null)}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
